// Finnish UI texts. UI strings are kept out of application logic
// so that further languages can be added later.
import type { AnyGameType, ArenaErrorCode, ErrorCode, GameEndReason, MultiEndReason, PresenceStatus, Role, TableEndReason } from '../../shared/protocol.js';
import type { Category } from '../../shared/games/dice/rules.js';
import type { SnakeEnd } from '../../shared/games/snake/index.js';
import type { TetraxEnd } from '../../shared/games/tetrax/index.js';
import type { PokerHand } from '../../shared/games/poker/index.js';
import type { ChessEnd } from '../../shared/games/chess/index.js';

import type { ShipType, ShotResult } from '../../shared/games/battleship/index.js';

import type { ExperienceLevel } from '../../shared/experience.js';

type Outcome = 'win' | 'loss' | 'none';

const errors: Record<ErrorCode | 'network', string> = {
  invalid_input: 'Tarkista syötetyt tiedot.',
  invalid_credentials: 'Väärä käyttäjätunnus tai salasana.',
  account_locked: 'Tunnus on lukittu. Ota yhteyttä ylläpitäjään.',
  too_many_attempts: 'Liian monta epäonnistunutta yritystä. Yritä myöhemmin uudelleen.',
  unauthorized: 'Kirjaudu sisään.',
  forbidden: 'Ei käyttöoikeutta.',
  forbidden_origin: 'Pyyntö estettiin. Avaa sivu osoitteesta https://areena.eu/ ja yritä uudelleen.',
  not_found: 'Ei löytynyt.',
  username_taken: 'Käyttäjätunnus on jo käytössä.',
  display_name_taken: 'Näyttönimi on jo käytössä.',
  cannot_modify_self: 'Et voi tehdä tätä muutosta omaan tunnukseesi.',
  cannot_restrict: 'Tätä käyttäjää ei voi rajoittaa.',
  user_busy: 'Pelaaja on pelissä tai pelipöydässä. Lukitse tunnus ja poista se, kun peli on päättynyt.',
  password_change_required: 'Vaihda ensin salasanasi.',
  same_password: 'Uuden salasanan on oltava eri kuin nykyinen.',
  weak_password: 'Salasanassa on oltava vähintään 8 merkkiä ja vähintään yksi erikoismerkki (esim. ! ? # %).',
  invalid_link: 'Linkki on vanhentunut, jo käytetty tai virheellinen. Pyydä ylläpitäjältä uusi linkki.',
  invalid_code: 'Kutsukoodi ei kelpaa. Tarkista koodi – se voi myös olla jo käytetty tai vanhentunut.',
  registration_closed: 'Rekisteröityminen kutsukoodilla on tällä hetkellä suljettu.',
  label_taken: 'Samalla tunnisteella on jo kutsukoodierä.',
  name_taken: 'Samalla nimellä on jo tallennettu – valitse toinen nimi.',
  refill_not_available: 'Päivän täydennys on jo käytetty tai saldo on jo täydennysrajalla.',
  insufficient_coins: 'AreCoin-saldo ei riitä.',
  tables_full: 'Blackjack-pöytiä on jo enimmäismäärä (4) – liity olemassa olevaan pöytään.',
  table_not_found: 'Pöytää ei enää ole.',
  seat_taken: 'Paikka on jo varattu.',
  already_seated: 'Istut jo toisessa blackjack-pöydässä.',
  not_seated: 'Et istu tässä pöydässä.',
  not_now: 'Ei nyt – odota vuoroasi.',
  bad_bet: 'Panos ei kelpaa (pöydän rajat ja 5:n välein).',
  arena_closed: 'Pelisali on suljettu huoltotauon ajaksi. Vieraana pääset sisään, kun pelisali avataan.',
  game_running: 'Bingopeli on jo käynnissä.',
  no_game: 'Bingopeliä ei ole käynnissä.',
  no_players: 'Erästä ei ole haettu yhtään kuponkia.',
  too_fast: 'Odota hetki – edellinen arvonta on vielä käynnissä.',
  all_drawn: 'Kaikki numerot on jo arvottu.',
  not_bingo: 'Kupongilla ei ole bingoa tässä vaiheessa.',
  batch_in_game: 'Erä on käynnissä olevassa pelissä.',
  auto_game: 'Käynnissä on automaattinen bingo – sitä ohjataan Automaattinen bingo -paneelista.',
  full: 'Bingo on täynnä – kuponkien haku on suljettu.',
  nickname_required: 'Nimimerkki puuttuu.',
  nickname_taken: 'Nimimerkki on jo käytössä.',
  limit_reached: 'Pelaajan kuponkiraja on täynnä.',
  player_unknown: 'Pelaajaa ei löytynyt.',
  code_taken: 'Koodi on jo toisen pelaajan käytössä.',
  code_voided: 'Koodi on mitätöity.',
  player_busy: 'Pelaajan kuponki on käynnissä olevassa pelissä tai hänen koodejaan lähetetään juuri.',
  invalid_phone: 'Puhelinnumero ei kelpaa.',
  invalid_email: 'Sähköpostiosoite ei kelpaa.',
  closed: 'Ilmoittautuminen on suljettu.',
  phone_limit: 'Tällä puhelinnumerolla on jo pyydetty enimmäismäärä koodeja (3).',
  email_limit: 'Tällä sähköpostiosoitteella on jo pyydetty enimmäismäärä koodeja (3).',
  ip_limit: 'Samasta verkosta on jo ilmoittauduttu enimmäismäärä tänään.',
  ip_nickname: 'Tästä verkosta on jo ilmoittauduttu toisella nimimerkillä.',
  bot_check: 'Bottitarkistus epäonnistui. Yritä uudelleen.',
  no_phone: 'Puhelimen osoite ja tunnukset puuttuvat (Puhelin-kohta).',
  no_mail: 'Sähköpostipalvelimen tunnus ja salasana puuttuvat (Sähköposti-kohta).',
  busy: 'Edellinen lähetys on vielä käynnissä.',
  not_enough_codes: 'Erässä ei ole tarpeeksi vapaita koodeja. Valitse toinen erän nimi tai vähemmän vastaanottajia.',
  nothing_selected: 'Valituissa ei ole lähetettäviä (odottavia tai epäonnistuneita).',
  internal_error: 'Palvelimella tapahtui virhe.',
  network: 'Yhteysvirhe. Tarkista verkkoyhteys.',
};

const roles: Record<Role, string> = {
  player: 'Pelaaja',
  moderator: 'Moderaattori',
  admin: 'Ylläpitäjä',
};

export const fi = {
  appName: 'Areena',
  errors,
  roles,

  footer: {
    copyright: '© 2026 K.Lahti',
    privacy: 'Tietosuojaseloste',
  },

  passwordChange: {
    title: 'Vaihda salasana',
    intro: 'Tunnuksesi on uusi tai salasanasi on nollattu. Valitse oma salasana ennen kuin jatkat peliareenalle.',
    current: 'Saamasi salasana',
    submit: 'Vaihda ja jatka',
  },

  closure: {
    title: 'Pelisali on suljettu',
    reopens: (when: string) => `Avautuu arviolta ${when}`,
    reopensUnknown: 'Avaamme pelisalin heti kun mahdollista.',
    countdown: 'Aukeamiseen',
    stayHint: 'Voit jättää sivun auki – se päivittyy itsestään, kun pelisali aukeaa.',
    loginNotice: 'Pelisali on suljettu',
    upcoming: (when: string, message: string) => `Huoltotauko alkaa ${when}: ${message}`,
    draining: (left: string, message: string) =>
      `Huoltotauko alkaa ${left} kuluttua – uusia pelejä ei voi aloittaa ja keskeneräiset pelit keskeytetään tauon alkaessa. ${message}`,
    gameAborted: 'Peli keskeytettiin huoltotauon vuoksi. Peliä ei lasketa tilastoihin.',
    offlineTitle: 'Areena päivittyy',
    offlineMessage: 'Palvelu on hetken pois käytöstä. Sivu palaa itsestään, kun Areena on taas käytettävissä.',
    adminClosed: 'Pelisali on suljettu muilta. Ylläpitäjänä voit käyttää sitä normaalisti.',
    adminLink: 'Hallitse',
  },

  login: {
    // Automatic logout (user decisions 2026-10-02).
    loggedOutIdle: 'Sinut kirjattiin ulos, koska et ollut aktiivinen 30 minuuttiin. Kirjaudu uudelleen.',
    loggedOutAway: 'Sinut kirjattiin ulos, koska Areena oli suljettuna yli 5 minuuttia. Kirjaudu uudelleen.',
    title: 'Kirjaudu sisään',
    subtitle: 'Tervetuloa peliareenalle.',
    username: 'Käyttäjätunnus',
    password: 'Salasana',
    submit: 'Kirjaudu',
    submitting: 'Kirjaudutaan…',
    noAccount: 'Tunnuksen saat ylläpitäjältä tai kutsukoodilla.',
    register: 'Onko sinulla kutsukoodi? Rekisteröidy',
  },

  // Public front page (user request 2026-09-30).
  landing: {
    navGames: 'Pelit',
    navFeatures: 'Areena',
    navJoin: 'Liity',
    login: 'Kirjaudu sisään',
    eyebrow: 'Kaveriporukan oma peliareena',
    title1: 'Pelaa, haasta ja',
    titleAccent: 'voita',
    title2: '– yhdessä.',
    lead: 'Areena on selaimessa toimiva pelisali: klassikkopelejä kahdestaan ja porukalla, reaaliaikaiset haasteet, chat ja oma tilastohistoria. Ei asennuksia – toimii tietokoneella ja puhelimella.',
    ctaLogin: 'Kirjaudu sisään',
    ctaGuest: 'Kokeile vieraana',
    ctaRegister: 'Rekisteröidy kutsukoodilla',
    statOnline: (n: number) => (n === 1 ? 'pelaaja paikalla' : 'pelaajaa paikalla'),
    statGames: (n: number) => (n === 1 ? 'peli käynnissä' : 'peliä käynnissä'),
    statPlayed: (n: number) => (n === 1 ? 'peli pelattu' : 'peliä pelattu'),
    gamesTitle: 'Pelit',
    gamesLead: 'Haasta kaveri, arvo satunnainen vastustaja tai mittaa taitosi tietokonetta vastaan.',
    badges: {
      duel: '2 pelaajaa',
      computer: 'Tietokone',
      solo: 'Yksinpeli',
      table: '1–6 pelaajaa',
      realtime: 'Reaaliaikainen',
      clock: 'Shakkikello',
      bluff: 'Bluffi',
    },
    featuresTitle: 'Kaikki mitä pelisaliin kuuluu',
    features: [
      { icon: '⚡', title: 'Reaaliaikaiset haasteet', text: 'Näet ketkä ovat paikalla ja haastat yhdellä napautuksella. Peli alkaa heti, kun haaste hyväksytään.' },
      { icon: '🏆', title: 'Tilastot ja tasot', text: 'Voitot, tappiot ja tasapelit kertyvät omaan historiaan. Kokemustaso nousee pelatessa.' },
      { icon: '💬', title: 'Chat ja yksityisviestit', text: 'Aulan keskustelu, pelin oma chat ja yksityisviestit – myös poissaoleville.' },
      { icon: '🎱', title: 'Bingoillat', text: 'Juonnettu bingo omilla kupongeilla ja upealla arvontanäkymällä, myös striimattavaksi.' },
      { icon: '🎵', title: 'Radio taustalle', text: 'Musiikki soi pelatessa, jos ylläpito on antanut siihen luvan.' },
      { icon: '🛡️', title: 'Turvallinen yhteisö', text: 'Kutsupohjainen palvelu, moderointi ja selkeät säännöt. Ei seurantaevästeitä.' },
    ],
    joinTitle: 'Tule mukaan',
    joinLead: 'Areena on kutsupohjainen. Tunnuksen saat ylläpidolta tai kutsukoodilla – vieraskoodilla pääset kokeilemaan heti.',
    joinCards: {
      register: { title: 'Kutsukoodi', text: 'Sait kutsukortin? Luo tunnus koodilla muutamassa sekunnissa.' },
      guest: { title: 'Vieraskoodi', text: 'Kokeile Areenaa 3 tunnin vierailulla ilman tunnusta.' },
      login: { title: 'Minulla on tunnus', text: 'Kirjaudu sisään ja jatka siitä mihin jäit.' },
    },
    radioTitle: (name: string) => `Kuuntele ${name}`,
    radioText: 'Areenan radio soi myös erillisessä soittimessa.',
    radioLink: 'Avaa soitin',
    showcase: 'Pelivitriini: shakki, othello, matopeli, nopat ja laivanupotus',
    closeLogin: 'Sulje',
  },

  password: {
    ruleLength: 'Vähintään 8 merkkiä',
    ruleSpecial: 'Vähintään yksi erikoismerkki (esim. ! ? # % &)',
    show: 'Näytä salasana',
    hide: 'Piilota salasana',
  },

  join: {
    linkTitle: 'Tervetuloa Areenaan!',
    linkIntro: 'Viimeistele tunnuksesi: valitse pelaajanimi ja salasana.',
    resetTitle: 'Uusi salasana',
    resetIntro: 'Valitse uusi salasana. Muut kirjautumisesi kirjataan ulos.',
    codeTitle: 'Rekisteröidy kutsukoodilla',
    codeIntro: 'Syötä saamasi kutsukoodi ja valitse käyttäjätunnus, pelaajanimi ja salasana.',
    code: 'Kutsukoodi',
    codeHint: '8 merkkiä, esim. ABCD2345',
    username: 'Käyttäjätunnus',
    usernameHint: 'Kirjautumista varten, ei näy muille. 3–32 merkkiä: a–z, 0–9, _ . -',
    displayName: 'Pelaajanimi',
    displayNameHint: 'Näkyy muille pelaajille Areenalla. 2–24 merkkiä.',
    password: 'Salasana',
    repeat: 'Salasana uudelleen',
    mismatch: 'Salasanat eivät täsmää.',
    linkValid: (when: string) => `Linkki on voimassa ${when} asti.`,
    codeValid: (when: string) => `Kutsukoodi on voimassa ${when} asti.`,
    submitJoin: 'Luo tunnus ja siirry Areenalle',
    submitReset: 'Tallenna ja kirjaudu',
    submitting: 'Tallennetaan…',
    checking: 'Tarkistetaan…',
    backToLogin: 'Takaisin kirjautumiseen',
    privacy: 'Tietojasi käsitellään tietosuojaselosteen mukaisesti.',
  },

  // Guests (decision 2026-09-29).
  guest: {
    loginLink: 'Onko sinulla vieraskoodi? Kokeile vieraana',
    title: 'Kokeile Areenaa vieraana',
    intro: 'Vieraskoodilla pääset pelaamaan kerran ilman tunnusta.',
    terms: [
      'Vierailu kestää enintään 3 tuntia tai kunnes kirjaudut ulos.',
      'Saat vieraana leikkinimen, esim. "Vieras Kettu".',
      'Voit pelata kaikkia pelejä ja lukea chattia, mutta et kirjoittaa siihen.',
      'Pelejäsi ei tallenneta tilastoihin, ja vierailun tiedot poistetaan sen päätyttyä.',
    ],
    code: 'Vieraskoodi',
    start: 'Aloita vierailu',
    starting: 'Aloitetaan…',
    badge: 'vieras',
    badgeTitle: 'Vieras – pelit eivät tallennu tilastoihin',
    timeLeft: (time: string) => `${time} jäljellä`,
    ended: 'Vierailusi on päättynyt. Kiitos kun kävit Areenalla!',
    readOnly: 'Vieraana voit lukea chattia, mutta et kirjoittaa.',
    logoutConfirm: 'Vierailusi päättyy, kun kirjaudut ulos, eikä samalla vieraskoodilla pääse enää takaisin. Kirjaudutaanko ulos?',
    accountTitle: 'Olet vieraana',
    accountText: (until: string) =>
      `Vierailusi päättyy viimeistään klo ${until}. Pelejäsi ei tallenneta tilastoihin, ja vierailun tiedot poistetaan, kun kirjaudut ulos tai aika päättyy.`,
    register: 'Haluatko oman tunnuksen? Pyydä kutsukoodi ylläpitäjältä.',
  },

  nav: {
    arena: 'Areena',
    game: 'Peli',
    account: 'Oma tili',
    admin: 'Ylläpito',
    bingo: 'Bingo',
    moderation: 'Moderointi',
    tables: 'Pelipöydät',
    messages: 'Viestit',
    messagesUnread: (n: number) => `${n} lukematonta yksityisviestiä`,
    logout: 'Kirjaudu ulos',
  },

  games: {
    battleship: {
      name: 'Laivanupotus',
      description: 'Klassinen kahden pelaajan meritaistelu 10 × 10 -ruudukolla.',
    },
    othello: {
      name: 'Othello',
      description: 'Käännä vastustajan kiekot omiksesi – enemmän kiekkoja voittaa.',
    },
    memory: {
      name: 'Muistipeli',
      description: 'Etsi parit 36 kortista. Löydetty pari antaa uuden vuoron.',
    },
    chess: {
      name: 'Shakki',
      description: 'Klassinen strategiapeli shakkikellolla. Haasta pelaaja tai tietokone viidellä tasolla.',
    },
    snake: {
      name: 'Matopeli',
      description: 'Vanhojen kännyköiden klassikko: syö omenoita ja kasva – älä törmää. Yksin tai kaksin.',
    },
    tetrax: {
      name: 'Tetrax',
      description: 'Putoavat palikat riveiksi – täysi rivi katoaa. Yksin ennätystä kohti tai kaksin roskarivejä vastustajalle.',
    },
    dice: {
      name: 'Nopparalli',
      description: 'Viisi noppaa, kolme heittoa, 15 kohtaa. 1–6 pelaajaa pelipöydässä.',
    },
    vale: {
      name: 'Valepaskahousu',
      description: 'Bluffikorttipeli: pelaa kortit kuvapuoli alas ja väitä mitä vain – jää kiinni ja nostat pinon. 2–6 pelaajaa.',
    },
  } satisfies Record<AnyGameType, { name: string; description: string }>,

  turn: {
    yours: 'Sinun vuorosi',
    theirs: (name: string) => `${name} miettii…`,
    timeBank: 'Aikapankki',
    opponentTimeBank: 'Vastustajan aikapankki',
  },

  othello: {
    you: 'Sinä',
    black: 'Musta',
    white: 'Valkoinen',
    yourColor: (c: string) => `Pelaat: ${c}`,
    passed: 'Vastustajalla ei ollut siirtoa – vuoro jatkuu.',
    youPassed: 'Sinulla ei ollut siirtoa – vastustaja jatkaa.',
    cell: (label: string) => `Ruutu ${label}`,
    discs: 'Kiekot',
  },

  memory: {
    pairs: 'Parit',
    you: 'Sinä',
    match: 'Pari!',
    noMatch: 'Ei paria.',
    card: (n: number) => `Kortti ${n}`,
    remaining: (n: number) => `Pareja jäljellä: ${n}`,
  },

  // Valepaskahousu (decision 2026-09-30).
  vale: {
    you: 'Sinä',
    deck: 'Pakka',
    pile: 'Pino',
    cardsLeft: (n: number) => `${n} korttia`,
    emptyTable: 'Pöytä on tyhjä – pelaa mitä vain.',
    claim: (n: number, rank: string) => `${n} × ${rank}`,
    run: (n: number, rank: string) => `pinossa jo ${n} × ${rank}`,
    burning: 'Pino kaatuu, jos kukaan ei epäile!',
    burnedTitle: 'Pino kaatui!',
    burned: (n: number) => `${n} korttia poistui pelistä. Kaataja jatkaa tyhjään pöytään.`,
    liar: 'Valehtelet!',
    doubtButton: 'EPÄILE!',
    doubts: (who: string) => `${who} epäilee!`,
    doubtedClaim: (who: string, n: number, rank: string) => `${who} väitti: ${n} × ${rank} – käännetään…`,
    claims: (who: string, n: number, rank: string) => `${who} väittää: ${n} × ${rank}`,
    doubtWindow: 'Epäiletkö? Aikaa on 7 sekuntia.',
    doubtUntil: (who: string) => `Ehdit epäillä, kunnes ${who} pelaa.`,
    truth: 'TOTTA',
    lie: 'VALETTA',
    doubtTrue: (doubter: string, accused: string, n: number) => `${accused} puhui totta – ${doubter} nostaa pinon (${n} korttia).`,
    doubtFalse: (doubter: string, accused: string, n: number) => `${doubter} paljasti valeen – ${accused} nostaa pinon (${n} korttia).`,
    burn: (who: string, reason: string) => `${who}: ${reason} – pino kaatuu, jos kukaan ei epäile.`,
    burnReason: { ten: 'kymppi', ace: 'ässä', four: 'neljä samaa' },
    emptyTrue: (who: string, receiver: string) => `${who} pelasi kaatokortin tyhjään pöytään – totta: ${receiver} saa sen käteensä, ${who} jatkaa.`,
    emptyFalse: (who: string) => `${who} pelasi kaatokortin tyhjään pöytään – valetta: kortti palaa takaisin.`,
    out: (who: string, place: number) => `${who} pääsi korteista eroon (${place}.)!`,
    claimAs: 'Väitä',
    selectCards: 'Valitse kortit kädestä',
    play: (n: number, rank: string, bluff: boolean) => `Pelaa ${n} × ${rank}${bluff ? ' (bluffi)' : ''}`,
    wait: (s: number) => `Muut voivat epäillä… ${s} s`,
    tooLate: 'Liian myöhään – tilanne ehti muuttua.',
    youAreOut: (place: number) => `Pääsit korteista eroon – sijoituksesi ${place}. Seuraa peliä loppuun.`,
    youArePaskahousu: 'Sinä olet paskahousu!',
    endReason: 'Peli päättyi – yksi jäi kortteineen.',
    survived: 'Selvisit!',
    isPaskahousu: (who: string) => `${who} on paskahousu!`,
  },

  // Chess (decision 2026-09-30).
  chess: {
    white: 'Valkea',
    black: 'Musta',
    youPlay: (c: string) => `Pelaat: ${c}`,
    yourMove: 'Sinun siirtosi',
    theirMove: (name: string) => `${name} miettii…`,
    check: 'Shakki!',
    clock: 'Shakkikello',
    clockLabel: (min: number, inc: number) => (inc > 0 ? `${min} min + ${inc} s` : `${min} min`),
    clockHint: 'Kummallakin on oma aikansa; jokainen siirto lisää kelloon lisäajan.',
    level: 'Tietokoneen taso',
    levels: ['', 'Aloittelija', 'Harrastaja', 'Kerhopelaaja', 'Vahva', 'Mestari'] as const,
    theme: 'Lauta',
    themes: { wood: 'Puu', dark: 'Tumma' },
    moves: 'Siirrot',
    noMoves: 'Ei siirtoja vielä.',
    promoteTitle: 'Korota sotilas',
    pieces: { q: 'Daami', r: 'Torni', b: 'Lähetti', n: 'Ratsu', p: 'Sotilas', k: 'Kuningas' },
    offerDraw: 'Tarjoa tasapeliä',
    drawOffered: 'Tasapelitarjous lähetetty – odotetaan vastausta.',
    drawDeclinedByAi: 'Tietokone ei hyväksynyt tasapeliä.',
    drawDeclined: 'Vastustaja ei hyväksynyt tasapeliä.',
    drawIncoming: (name: string) => `${name} tarjoaa tasapeliä.`,
    drawAccept: 'Hyväksy tasapeli',
    drawDecline: 'Hylkää',
    square: (name: string) => `Ruutu ${name}`,
    boardLabel: 'Shakkilauta',
    end: {
      checkmate: (o: Outcome) => (o === 'win' ? 'Matti! Voitit.' : 'Matti – kuninkaasi on kaatunut.'),
      stalemate: () => 'Patti: siirtovuorossa olevalla ei ole laillisia siirtoja, mutta kuningas ei ole shakissa.',
      repetition: () => 'Sama asema toistui kolmesti.',
      fifty_moves: () => '50 siirtoa ilman lyöntiä tai sotilaan siirtoa.',
      insufficient_material: () => 'Kummallakaan ei ole enää riittävästi nappuloita mattiin.',
      timeout_insufficient: () => 'Aika loppui, mutta vastustajalla ei ollut nappuloita mattiin.',
    } satisfies Record<ChessEnd, (o: Outcome) => string>,
  },

  snake: {
    you: 'Sinä',
    length: 'Pituus',
    apples: 'Omenat',
    timeLeft: 'Aikaa',
    best: 'Ennätys',
    newBest: 'Uusi ennätys!',
    theme: 'Ulkoasu',
    themes: { lcd: 'Retro-LCD', neon: 'Neon' },
    countdown: 'Valmiina…',
    go: 'Nyt!',
    crashed: 'Törmäys!',
    soloTitle: 'Matopeli – yksinpeli',
    soloStart: 'Aloita',
    soloAgain: 'Uusi peli',
    soloOver: (score: number) => `Peli päättyi – söit ${score} omenaa.`,
    soloHint: 'Nuolinäppäimet / WASD tai pyyhkäisy. Välilyönti aloittaa.',
    playSolo: 'Pelaa yksin',
    back: 'Takaisin aulaan',
    controls: { up: 'Ylös', down: 'Alas', left: 'Vasemmalle', right: 'Oikealle' },
    boardLabel: 'Matopelin lauta',
    offline: 'yhteys katkennut',
    resultScore: (mine: number, theirs: number) => `Pituudet ${mine}–${theirs}`,
    end: {
      crash: (o: Outcome) => (o === 'win' ? 'Vastustajan mato törmäsi.' : 'Matosi törmäsi.'),
      both_crashed: (o: Outcome) =>
        o === 'none' ? 'Molemmat törmäsivät, ja madot olivat yhtä pitkiä.' : o === 'win' ? 'Molemmat törmäsivät – sinun matosi oli pidempi.' : 'Molemmat törmäsivät – vastustajan mato oli pidempi.',
      time_up: (o: Outcome) =>
        o === 'none' ? 'Aika loppui, ja madot olivat yhtä pitkiä.' : o === 'win' ? 'Aika loppui – sinun matosi oli pidempi.' : 'Aika loppui – vastustajan mato oli pidempi.',
    } satisfies Record<SnakeEnd, (o: Outcome) => string>,
  },

  tetrax: {
    you: 'Sinä',
    score: 'Pisteet',
    lines: 'Rivit',
    level: 'Taso',
    next: 'Seuraavat',
    hold: 'Säilö',
    best: 'Ennätys',
    newBest: 'Uusi ennätys!',
    timeLeft: 'Aikaa',
    incoming: 'Roskarivejä tulossa',
    go: 'Nyt!',
    paused: 'Tauko – P jatkaa',
    toppedOut: 'Pino nousi yli!',
    waiting: 'Odotetaan vastustajaa…',
    soloTitle: 'Tetrax – yksinpeli',
    soloStart: 'Aloita',
    soloAgain: 'Uusi peli',
    soloOver: (score: number, lines: number) => `Peli päättyi – ${score} pistettä, ${lines} riviä.`,
    soloHint: 'Välilyönti tai Enter aloittaa. P tai Esc pitää tauon.',
    keys: '← → siirto · ↑ tai X kierto · Z vastapäivään · ↓ pehmeä pudotus · välilyönti pudotus · C säilö',
    boardLabel: 'Tetraxin pelikenttä',
    opponentBoard: (name: string) => `Vastustajan ${name} kenttä`,
    offline: 'yhteys katkennut',
    resultScore: (mine: number, theirs: number) => `Pisteet ${mine}–${theirs}`,
    end: {
      topped_out: (o: Outcome) => (o === 'win' ? 'Vastustajan pino nousi yli.' : 'Pinosi nousi yli.'),
      time_up: (o: Outcome) =>
        o === 'none' ? 'Aika loppui, ja pisteet olivat tasan.' : o === 'win' ? 'Aika loppui – sinulla oli enemmän pisteitä.' : 'Aika loppui – vastustajalla oli enemmän pisteitä.',
    } satisfies Record<TetraxEnd, (o: Outcome) => string>,
  },

  poker: {
    title: 'Pokeri',
    tableCard: 'Pokeriautomaatti',
    tableCardDesc: 'Kioskien klassikko: jokeri, voitto kahdesta parista ja tuplaus – AreCoineilla.',
    play: 'Pelaa pokeria',
    back: 'Pelipöytiin',
    hands: {
      five: 'Viitoset',
      straight_flush: 'Värisuora',
      four: 'Neloset',
      full_house: 'Täyskäsi',
      flush: 'Väri',
      straight: 'Suora',
      three: 'Kolmoset',
      two_pairs: 'Kaksi paria',
    } satisfies Record<PokerHand, string>,
    bet: 'Panos',
    win: 'Voitto',
    balance: 'Saldo',
    deal: 'Jako',
    draw: 'Vaihda',
    double: 'Tuplaus',
    collect: 'Voitonmaksu',
    small: 'Pieni (A–6)',
    big: 'Iso (8–K)',
    held: 'Lukittu',
    joker: 'Jokeri',
    startHint: 'Valitse panos ja paina JAKO.',
    holdHint: 'Napauta kortit, jotka pidät, ja paina VAIHDA.',
    winHint: (win: number) => `Voitit ${win}! Tuplaa tai ota voitto.`,
    topHint: 'Voitto on yli puolet päävoitosta – tuplaus ei ole enää mahdollinen.',
    doubleHint: 'Onko seuraava kortti pieni (A–6) vai iso (8–K)? Seiska häviää, jokeri voittaa.',
    doubleWin: (win: number) => `Oikein! Voitto nyt ${win}.`,
    doubleLose: 'Väärin – voitto meni.',
    blackSeven: 'Musta seiska – voitto meni.',
    redSeven: 'Punainen seiska – tuplaus päättyi, saat voiton.',
    noWin: 'Ei voittoa.',
    paid: (n: number) => `Saldoon ${n}.`,
    keys: 'Näppäimet: 1–5 lukitse · Enter jako / vaihda · T tuplaus · ← pieni · → iso · V voitonmaksu · + panos',
    rules: [
      'Pakassa on 52 korttia ja jokeri, joka käy minä tahansa korttina. Ässä on suorassa 1 tai 14.',
      'Saat viisi korttia. Lukitse pidettävät ja vaihda loput kerran. Voittoon tarvitaan vähintään kaksi paria.',
      'Voitot ovat panos kertaa taulukon kerroin. Päävoitto (viitoset tai värisuora) on 50 × panos.',
      'Voiton jälkeen voit tuplata, kun voitto on enintään puolet päävoitosta: arvaa, onko seuraava kortti pieni (A–6) vai iso (8–K). Ässä on tuplauksessa aina 1. Jokeri voittaa aina, punainen seiska päättää tuplauksen ja saat voiton, musta seiska häviää.',
      'Uusi jako maksaa odottavan voiton saldoon. Pelaa leikkirahalla: AreCoineja ei voi ostaa eikä vaihtaa rahaksi.',
    ],
  },

  arena: {
    chooseGame: 'Valitse peli',
    heroSubtitle: 'Valitse peli, haasta vastustaja – tai pelaa tietokonetta vastaan.',
    players: (game: string) => `Pelaajat – ${game}`,
    acceptChallenges: 'Otan vastaan haasteita',
    yourStatus: 'Tilasi',
    status: {
      available: 'Vapaa',
      idle: 'Poissa',
      in_game: 'Pelissä',
      not_accepting: 'Ei ota haasteita',
      waiting: 'Odottaa peliä',
    } satisfies Record<PresenceStatus, string>,
    randomOpponent: 'Satunnainen vastustaja',
    playComputer: 'Pelaa tietokonetta vastaan',
    challenge: 'Haasta',
    noPlayers: 'Muita pelaajia ei ole paikalla juuri nyt.',
    waitingFor: (name: string) => `Odotetaan vastausta pelaajalta ${name}…`,
    incomingTitle: 'Haaste!',
    incomingText: (name: string, game: string) => `${name} haastaa sinut peliin: ${game}`,
    accept: 'Hyväksy',
    decline: 'Hylkää',
    notices: {
      declined: (name: string) => `${name} hylkäsi haasteesi.`,
      expiredOut: (name: string) => `Haaste pelaajalle ${name} vanheni.`,
      expiredIn: (name: string) => `Pelaajan ${name} haaste vanheni.`,
      accepted: (name: string) => `${name} hyväksyi haasteesi!`,
      cancelled: (name: string) => `Haaste (${name}) peruuntui.`,
    },
    errors: {
      invalid_input: 'Virheellinen pyyntö.',
      not_available: 'Pelaaja ei ole juuri nyt haastettavissa.',
      self_challenge: 'Et voi haastaa itseäsi.',
      already_in_game: 'Olet jo pelissä.',
      challenge_pending: 'Sinulla on jo haaste odottamassa vastausta.',
      no_opponents: 'Vapaita vastustajia ei löytynyt juuri nyt.',
      arena_full: 'Areena on täynnä. Yritä hetken kuluttua uudelleen.',
      challenge_not_found: 'Haastetta ei enää ole.',
      not_in_game: 'Et ole pelissä.',
      draw_not_available: 'Tasapeliä ei voi tarjota nyt (tarjous on jo tehty tai hylätty – tee ensin siirto).',
      internal_error: 'Palvelimella tapahtui virhe.',
      wrong_phase: 'Toiminto ei ole mahdollinen tässä pelin vaiheessa.',
      not_your_turn: 'Ei ole sinun vuorosi.',
      invalid_move: 'Virheellinen siirto.',
      already_shot: 'Olet jo ampunut tähän ruutuun.',
      invalid_setup: 'Laivasto ei ole sääntöjen mukainen.',
      setup_already_done: 'Laivasto on jo vahvistettu.',
      too_fast: 'Liian monta viestiä lyhyessä ajassa. Odota hetki.',
      chat_closed: 'Aulan chat on suljettu.',
      arena_closed: 'Pelisali on suljettu huoltotauon ajan.',
      arena_closing: 'Huoltotauko alkaa pian – uusia pelejä ei voi aloittaa.',
      message_too_long: 'Viesti on liian pitkä (enintään 160 merkkiä).',
      links_not_allowed: 'Linkkien jakaminen chatissa ei ole sallittua.',
      chat_muted: 'Sinulla on chat-esto.',
      guest_read_only: 'Vieraana voit lukea chattia, mutta et kirjoittaa.',
      game_banned: 'Sinulla on pelikielto.',
      table_not_found: 'Pelipöytää ei enää ole.',
      table_full: 'Pelipöytä on täynnä.',
      not_table_host: 'Vain pöydän avaaja voi tehdä tämän.',
      already_at_table: 'Olet jo pelipöydässä.',
      not_enough_players: 'Pelaajia on liian vähän.',
    } satisfies Record<ArenaErrorCode, string>,
    cancel: 'Peru',
  },

  battleship: {
    placementTitle: 'Sijoita laivastosi',
    placementHelp:
      'Valitse laiva telakasta ja asento, vie laiva laudalle ja napsauta. Asetettua laivaa voi raahata, kääntää (R) ja poistaa (Delete). Laivat eivät saa koskea toisiaan, eivät edes kulmittain.',
    placementHelpTouch:
      'Valitse laiva telakasta ja asento, sitten vedä laiva sormella paikalleen. Asetettua laivaa voi vetää, kääntää ja poistaa.',
    removeShip: 'Poista laiva',
    cannotRotate: 'Laiva ei mahdu kääntymään tähän – siirrä sitä ensin.',
    timeLeft: 'Aikaa jäljellä',
    rotate: 'Käännä',
    orientation: 'Laivan asento',
    horizontal: 'Vaaka',
    vertical: 'Pysty',
    chooseShip: 'Valitse laiva',
    random: 'Satunnainen sijoitus',
    clear: 'Tyhjennä',
    ready: 'Valmis',
    shipsLeftToPlace: (n: number) => (n === 0 ? 'Kaikki laivat sijoitettu.' : `Sijoittamatta ${n} laivaa.`),
    waitingOpponent: 'Laivastosi on valmis. Odotetaan vastustajaa…',
    opponentReady: 'Vastustaja on valmis.',
    opponentPlacing: 'Vastustaja sijoittaa laivojaan.',
    shipNames: {
      battleship: 'Taistelulaiva',
      cruiser: 'Risteilijä',
      destroyer: 'Hävittäjä',
      patrol: 'Vartiovene',
    } satisfies Record<ShipType, string>,
    ownBoard: 'Oma laivasto',
    targetBoard: 'Vastustajan vesialue',
    yourTurn: 'Sinun vuorosi – ammu!',
    opponentTurn: (name: string) => `${name} ampuu…`,
    timeBank: 'Aikapankki',
    opponentTimeBank: 'Vastustajan aikapankki',
    shipsRemaining: 'Laivoja jäljellä',
    opponentOffline: 'Vastustajan yhteys on katki. Peli jatkuu.',
    lastShot: {
      miss: 'Ohi.',
      hit: 'Osuma!',
      sunk: 'Upposi!',
    } satisfies Record<ShotResult, string>,
    cell: (label: string) => `Ruutu ${label}`,
  },

  game: {
    opponent: 'Vastustaja',
    resign: 'Luovuta',
    cancel: 'Peruuta',
    resignTitle: 'Luovutetaanko peli?',
    resignText: 'Haluatko varmasti luovuttaa? Luovuttaminen päättää pelin ja vastustajasi voittaa.',
    placementPhase: 'Laivojen sijoitus – aikaa jäljellä',
    placementInfo: 'Jos laivasto ei ole valmis ajoissa, peli hävitään.',
  },

  result: {
    title: { win: 'Voitit!', loss: 'Hävisit', none: 'Peli päättyi' },
    score: (mine: number, theirs: number) => `Tulos ${mine}–${theirs}`,
    reason: {
      all_ships_sunk: (o: Outcome) => (o === 'win' ? 'Upotit koko vastustajan laivaston.' : 'Koko laivastosi upotettiin.'),
      resignation: (o: Outcome) => (o === 'win' ? 'Vastustaja luovutti.' : 'Luovutit pelin.'),
      turn_timeout: (o: Outcome) => (o === 'win' ? 'Vastustajan aika loppui.' : 'Aikasi loppui.'),
      placement_timeout: (o: Outcome) =>
        o === 'none'
          ? 'Kumpikaan ei saanut laivastoa valmiiksi ajoissa.'
          : o === 'win'
            ? 'Vastustaja ei saanut laivastoaan valmiiksi ajoissa.'
            : 'Laivastosi ei ollut valmis ajoissa.',
      aborted: () => 'Ylläpitäjä keskeytti pelin. Peliä ei lasketa tilastoihin.',
      game_complete: (o: Outcome) => (o === 'win' ? 'Voitit pisteillä!' : o === 'loss' ? 'Vastustaja voitti pisteillä.' : 'Tasapeli!'),
      draw_agreed: () => 'Tasapeli sovittiin.',
    } satisfies Record<GameEndReason, (o: Outcome) => string>,
    backToArena: 'Takaisin areenalle',
    showBoard: 'Katso lopputilanne',
    ended: (title: string, reason: string) => `Peli päättyi – ${title} ${reason}`,
  },

  levels: {
    beginner: 'Aloittelija',
    player: 'Pelaaja',
    experienced: 'Kokenut',
    veteran: 'Veteraani',
    expert: 'Mestari',
  } satisfies Record<ExperienceLevel, string>,

  chat: {
    title: 'Chat',
    placeholder: 'Kirjoita viesti…',
    send: 'Lähetä',
    empty: 'Ei viestejä vielä. Viestit poistetaan pelin päätyttyä.',
    close: 'Sulje',
    you: 'Sinä',
  },

  rules: {
    button: 'Pelin ohjeet',
    buttonFor: (game: string) => `${game} – ohjeet`,
    buttonShort: 'Ohjeet',
    title: (game: string) => `${game} – ohjeet`,
    close: 'Sulje',
    thisGame: 'Tämän pelin asetukset',
    turnTime: (s: number) => `Vuoroaika ${s} s`,
    timeBank: (s: number) => `aikapankki ${s} s`,
    extraShotOn: 'Osuma antaa lisälaukauksen.',
    extraShotOff: 'Vuoro vaihtuu jokaisen laukauksen jälkeen.',
    games: {
      battleship: [
        {
          heading: 'Tavoite',
          items: ['Upota vastustajan koko laivasto ennen kuin hän upottaa sinun laivastosi.'],
        },
        {
          heading: 'Lauta ja laivasto',
          items: [
            'Lauta on 10 × 10 ruutua: sarakkeet A–J ja rivit 1–10 (A1 on vasen alakulma).',
            'Kummallakin on 10 laivaa: 1 taistelulaiva (4 ruutua), 2 risteilijää (3), 3 hävittäjää (2) ja 4 vartiovenettä (1).',
          ],
        },
        {
          heading: 'Laivojen sijoitus',
          items: [
            'Valitse laiva ja asento (Vaaka tai Pysty – tietokoneella myös R-näppäin), vie laiva laudalle ja napsauta. Kosketusnäytöllä vedä laiva sormella paikalleen.',
            'Laivat sijoitetaan vaaka- tai pystysuoraan, ei vinottain.',
            'Laivat eivät saa koskea toisiaan – eivät edes kulmittain. Kielletty alue näytetään raidoitettuna, ja vihreä esikatselu kertoo sallitun paikan.',
            'Asetettu laiva jää valituksi: sitä voi raahata uuteen paikkaan, kääntää ("Käännä" tai R) ja poistaa ("Poista laiva", Delete tai vetämällä pois laudalta). Seuraavan laivan valitset itse telakasta – missä järjestyksessä tahansa.',
            '"Satunnainen sijoitus" asettaa koko laivaston kerralla, "Tyhjennä" poistaa kaikki.',
            'Paina lopuksi "Valmis". Sijoitukseen on rajattu aika – jos laivasto ei ole valmis ajoissa, häviät pelin.',
          ],
        },
        {
          heading: 'Pelin kulku',
          items: [
            'Pelaajat ampuvat vuorotellen yhteen ruutuun vastustajan vesialueella.',
            'Harmaa ruutu = ohi, punainen = osuma, tummansininen laiva = upotettu laiva.',
            'Upotetun laivan ympärillä ei voi olla muita laivoja, joten ne ruudut merkitään tyhjiksi.',
            'Omalla laudallasi näet vastustajan laukaukset ja laivojesi tilanteen.',
          ],
        },
        {
          heading: 'Voitto',
          items: ['Se, joka upottaa ensin vastustajan kaikki laivat, voittaa.'],
        },
        {
          heading: 'Vinkkejä',
          items: [
            'Kun saat osuman, ammu viereisiin ruutuihin. Kun laivan suunta selviää, jatka sen päihin.',
            'Levitä ensimmäiset laukaukset laudalle – isoja laivoja on vähän, mutta ne vievät paljon tilaa.',
          ],
        },
      ],
      othello: [
        {
          heading: 'Tavoite',
          items: ['Pelin lopussa laudalla on enemmän sinun värisiäsi kiekkoja kuin vastustajan.'],
        },
        {
          heading: 'Aloitus',
          items: [
            'Lauta on 8 × 8 ruutua, ja keskellä on aluksi kaksi mustaa ja kaksi valkoista kiekkoa.',
            'Musta aloittaa. Pelin aloittaja pelaa mustilla.',
          ],
        },
        {
          heading: 'Siirto',
          items: [
            'Aseta kiekko tyhjään ruutuun niin, että vastustajan kiekkoja jää suoraan linjaan oman uuden kiekkosi ja jonkin vanhan kiekkosi väliin – vaaka-, pysty- tai vinosuunnassa.',
            'Kaikki väliin jäävät vastustajan kiekot kääntyvät sinun värisiksesi. Yhdellä siirrolla voi kääntää kiekkoja useaan suuntaan.',
            'Siirron on käännettävä vähintään yksi kiekko. Sallitut ruudut näkyvät vuorollasi pieninä pisteinä.',
          ],
        },
        {
          heading: 'Ohivuoro ja pelin loppu',
          items: [
            'Jos pelaajalla ei ole yhtään sallittua siirtoa, vuoro siirtyy automaattisesti vastustajalle.',
            'Peli päättyy, kun kumpikaan ei voi enää siirtää (yleensä kun lauta on täynnä).',
            'Enemmän kiekkoja saanut voittaa. Jos kiekkoja on yhtä paljon, peli päättyy tasapeliin.',
          ],
        },
        {
          heading: 'Vinkkejä',
          items: [
            'Kulmat ovat arvokkaimpia ruutuja: kulmakiekkoa ei voi koskaan kääntää.',
            'Varo kulmien viereisiä ruutuja – ne antavat usein vastustajalle pääsyn kulmaan.',
            'Monta kiekkoa pelin alussa ei ole tärkeää; tärkeämpää on, että sinulla on hyviä siirtovaihtoehtoja.',
          ],
        },
      ],
      memory: [
        {
          heading: 'Tavoite',
          items: ['Löydä enemmän korttipareja kuin vastustaja. Pöydällä on 36 korttia eli 18 paria.'],
        },
        {
          heading: 'Vuoro',
          items: [
            'Käännä kaksi korttia vuorollasi napauttamalla niitä.',
            'Jos kortit ovat pari, ne jäävät auki, saat parin itsellesi ja saat jatkaa vuoroa.',
            'Jos kortit eivät ole pari, ne näkyvät hetken molemmille ja kääntyvät takaisin. Vuoro siirtyy vastustajalle.',
            'Jokaisella kortin käännöllä on oma vuoroaikansa.',
          ],
        },
        {
          heading: 'Pelin loppu',
          items: [
            'Peli päättyy, kun kaikki parit on löydetty.',
            'Enemmän pareja löytänyt voittaa. Tasamäärällä peli päättyy tasapeliin.',
          ],
        },
        {
          heading: 'Vinkkejä',
          items: [
            'Muista myös vastustajan kääntämät kortit – niistä on sinulle hyötyä.',
            'Jos tiedät jo parin toisen kortin paikan, käännä ensin uusi kortti ja sitten sen pari.',
          ],
        },
      ],
      chess: [
        {
          heading: 'Tavoite',
          items: [
            'Tee vastustajan kuninkaalle matti: kuningas on uhattuna (shakki) eikä pääse uhkaa pakoon.',
            'Valkea aloittaa. Värit arvotaan pelin alussa.',
          ],
        },
        {
          heading: 'Siirtäminen',
          items: [
            'Napauta nappulaa – lailliset kohderuudut näkyvät pisteinä – ja napauta kohderuutua. Nappulaa voi myös raahata.',
            'Kun sotilas pääsee viimeiselle riville, valitse mihin se korotetaan (yleensä daami).',
            'Linnoitus: siirrä kuningasta kaksi ruutua tornin suuntaan. Myös ohestalyönti toimii sääntöjen mukaan.',
            'Areena hyväksyy vain lailliset siirrot, joten et voi vahingossa jättää kuningasta shakkiin.',
          ],
        },
        {
          heading: 'Shakkikello',
          items: [
            'Haastaja valitsee ajan ennen haastetta, esimerkiksi 10 min + 5 s: kummallakin on 10 minuuttia, ja jokainen siirto lisää kelloon 5 sekuntia.',
            'Vain siirtovuorossa olevan kello käy. Jos aikasi loppuu, häviät – paitsi jos vastustajalla ei ole nappuloita mattiin, jolloin peli on tasapeli.',
          ],
        },
        {
          heading: 'Tasapeli',
          items: [
            'Patti, kolmesti toistunut asema, 50 siirtoa ilman lyöntiä tai sotilaan siirtoa ja riittämätön materiaali päättävät pelin tasapeliin.',
            'Voit tarjota tasapeliä pelin aikana. Vastustaja hyväksyy tai hylkää; siirron tekeminen hylkää tarjouksen. Hylätyn tarjouksen jälkeen voit tarjota uudelleen oman seuraavan siirtosi jälkeen.',
            'Tietokone hyväksyy tasapelin vain tasaisessa asemassa, aikaisintaan 20. siirron jälkeen.',
          ],
        },
        {
          heading: 'Tietokone',
          items: [
            'Tietokoneella on viisi tasoa aloittelijasta mestariin. Valitse taso aulassa ennen peliä.',
            'Tietokonetta vastaan pelaamasi pelit lasketaan tilastoihin kuten muutkin.',
          ],
        },
      ],
      tetrax: [
        {
          heading: 'Tavoite',
          items: [
            'Seitsemän erimuotoista palikkaa putoaa kentälle yksi kerrallaan. Siirrä ja kierrä niitä niin, että ne muodostavat täysiä vaakarivejä.',
            'Täysi rivi katoaa. Mitä useamman rivin poistat kerralla, sitä enemmän pisteitä – neljä riviä kerralla (Tetrax) on paras.',
            'Peli päättyy, kun uusi palikka ei enää mahdu kentälle.',
          ],
        },
        {
          heading: 'Ohjaus',
          items: [
            '← ja → siirtävät, ↑ tai X kiertää myötäpäivään, Z vastapäivään.',
            '↓ pudottaa nopeammin (1 piste / rivi), välilyönti pudottaa heti pohjaan (2 pistettä / rivi).',
            'C tai Shift laittaa palikan säilöön ja ottaa säilössä olleen (kerran palikkaa kohden).',
            'Haalea varjo näyttää, mihin palikka putoaa. Oikealla näkyvät viisi seuraavaa palikkaa.',
          ],
        },
        {
          heading: 'Pisteet ja tasot',
          items: [
            'Rivit: 1 = 100, 2 = 300, 3 = 500, 4 = 800 pistettä kerrottuna tasolla.',
            'Taso nousee joka 10. rivillä, ja palikat putoavat aina nopeammin.',
          ],
        },
        {
          heading: 'Kaksinpeli',
          items: [
            'Molemmilla on oma kenttä ja samat palikat samassa järjestyksessä. Vastustajan kenttä näkyy pienenä vieressä.',
            'Kun poistat useamman rivin kerralla, vastustajalle lähtee roskarivejä: 2 riviä → 1, 3 riviä → 2, 4 riviä → 4.',
            'Tulevat roskarivit näkyvät punaisena palkkina kentän vieressä. Ne nousevat kentän pohjalle, kun seuraava palikkasi asettuu ilman täyttä riviä – rivin poistamalla voit torjua ne.',
            'Se, jonka pino nousee ensin yli, häviää. Peli kestää enintään 10 minuuttia; silloin enemmän pisteitä voittaa.',
            'Tietokonevastustaja kiihtyy pelin edetessä.',
          ],
        },
        {
          heading: 'Yksinpeli',
          items: ['Pelaa yksin ennätystä kohti. P tai Esc pitää tauon. Paras tuloksesi tallentuu tähän selaimeen.'],
        },
      ],
      snake: [
        {
          heading: 'Tavoite',
          items: [
            'Ohjaa matoa ja syö omenoita. Jokainen omena kasvattaa matoa yhdellä.',
            'Laudan reunat ovat seiniä. Törmäys seinään, omaan häntään tai toiseen matoon päättää pelin.',
          ],
        },
        {
          heading: 'Ohjaus',
          items: [
            'Tietokoneella nuolinäppäimet tai W, A, S, D.',
            'Puhelimessa pyyhkäise laudalla tai käytä nuolipainikkeita laudan alla.',
            'Mato ei voi kääntyä suoraan taaksepäin. Kaksi nopeaa käännöstä peräkkäin ehtii jonoon.',
          ],
        },
        {
          heading: 'Kaksinpeli',
          items: [
            'Molemmat madot ovat samalla laudalla ja liikkuvat samaan aikaan. Peli alkaa 3–2–1-lähtölaskennalla.',
            'Kun toinen törmää, toinen voittaa. Jos molemmat törmäävät samalla hetkellä (esim. päät vastakkain), pidempi mato voittaa; tasapituus on tasapeli.',
            'Peli kestää enintään 10 minuuttia. Jos molemmat ovat silloin elossa, pidempi mato voittaa.',
            'Peli ei pysähdy yhteyskatkon ajaksi – mato jatkaa suoraan.',
          ],
        },
        {
          heading: 'Yksinpeli',
          items: ['Harjoittele yksin: pisteet ovat syötyjä omenoita. Paras tuloksesi tallentuu tähän selaimeen.'],
        },
        {
          heading: 'Vinkkejä',
          items: [
            'Pidä pää poissa vastustajan pään vierestä – nokkakolarissa kumpikaan ei yleensä voita.',
            'Pitkällä madolla kannattaa kiertää laitoja pitkin, ettei jää oman häntänsä sisään.',
          ],
        },
      ],
      vale: [
        {
          heading: 'Tavoite',
          items: [
            'Pääse korteistasi eroon. Viimeiseksi kortteineen jäävä on paskahousu.',
            'Jokaiselle jaetaan 5 korttia. Pelattujen tilalle nostetaan pakasta takaisin viiteen, niin kauan kuin pakkaa riittää.',
          ],
        },
        {
          heading: 'Vuoro',
          items: [
            'Pelaa vuorollasi yksi tai useampi kortti kuvapuoli alaspäin ja väitä niiden arvo, esim. "kolme seiskaa". Väitteen ei tarvitse olla totta – vuorolla on aina pakko pelata.',
            'Väitteen on oltava vähintään pinon päällimmäisen väitteen arvoinen.',
            'Kakkonen käy kaiken päälle, mutta kakkosen päälle käy vain kakkonen.',
          ],
        },
        {
          heading: 'Pinon kaatuminen',
          items: [
            'Kymppi (vain 3–9:n päälle) ja ässä (vain kuvakortin päälle) kaatavat pinon.',
            'Myös neljä samaa peräkkäin kaataa pinon, vaikka ne tulisivat useammalta pelaajalta. Kakkosia tämä ei koske.',
            'Kaatavaa erää voi epäillä 7 sekuntia. Jos kukaan ei epäile, pino kaatuu ja kortit poistuvat pelistä, ja kaatanut pelaaja jatkaa tyhjään pöytään.',
            'Kaatava kortti tyhjään pöytään tarkistetaan heti: jos se oli totta, seuraava pelaaja saa sen käteensä ja pelaaja jatkaa; jos valetta, kortti palaa pelaajalle.',
          ],
        },
        {
          heading: 'Valehtelet!',
          items: [
            'Kuka tahansa voi epäillä viimeksi pelattua erää niin kauan kuin seuraava ei ole pelannut. Seuraava joutuu odottamaan vähintään 7 sekuntia.',
            'Epäilyn jälkeen kaikki näkevät hetken kuka epäilee, ja sitten epäillyt kortit käännetään kaikkien nähtäville.',
            'Jos väite oli valetta, valehtelija nostaa koko pinon. Jos totta, epäilijä nostaa koko pinon.',
            'Pinon nostaja menettää vuoronsa.',
            'Pöydän avaaja valitsee vuoroajan (10 s – 2 min). Jos aika loppuu, peli pelaa puolestasi.',
          ],
        },
        {
          heading: 'Vinkkejä',
          items: [
            'Seuraa, montako kutakin arvoa on jo väitetty – yhdessä pakassa on vain neljä samaa.',
            'Valehtele mieluiten silloin, kun pino on pieni.',
          ],
        },
      ],
      dice: [
        {
          heading: 'Tavoite',
          items: ['Kerää eniten pisteitä täyttämällä pistetaulukon 15 kohtaa viiden nopan yhdistelmillä.'],
        },
        {
          heading: 'Vuoro',
          items: [
            'Omalla vuorollasi heität viittä noppaa enintään kolme kertaa.',
            'Heittojen välissä voit pitää haluamasi nopat napauttamalla niitä – pidetyt nopat eivät muutu seuraavassa heitossa.',
            'Vuoro päättyy, kun kirjaat pisteet yhteen vapaaseen kohtaan. Jokainen kohta täytetään kerran; jos nopat eivät sovi, kohta yliviivataan (0 pistettä).',
          ],
        },
        {
          heading: 'Yläosa',
          items: [
            'Ykköset–kuutoset: kyseisten silmälukujen summa (esim. kolme nelosta = 12).',
            'Bonus 50 pistettä, jos yläosan summa on vähintään 63 (keskimäärin kolme samaa jokaisesta).',
          ],
        },
        {
          heading: 'Alaosa',
          items: [
            'Pari: kahden saman summa. Kaksi paria: kahden eri parin summa.',
            'Kolme samaa ja neljä samaa: kyseisten noppien summa.',
            'Pieni suora 1–2–3–4–5 = 15 pistettä, iso suora 2–3–4–5–6 = 20 pistettä.',
            'Täyskäsi: kolme samaa ja pari (eri silmäluvut), kaikkien noppien summa.',
            'Sattuma: kaikkien noppien summa, mikä tahansa yhdistelmä.',
            'Täysosuma: viisi samaa = 50 pistettä.',
          ],
        },
        {
          heading: 'Pelin kulku ja voitto',
          items: [
            'Pelaajat pelaavat vuorotellen arvotussa järjestyksessä, kunnes kaikkien taulukot ovat täynnä (15 kierrosta).',
            'Eniten pisteitä kerännyt voittaa. Tasapisteissä sijoitus jaetaan.',
          ],
        },
        {
          heading: 'Vinkkejä',
          items: [
            'Tavoittele alussa kolmea samaa yläosan kohtiin – 63 pisteen bonus ratkaisee usein pelin.',
            'Säästä Sattuma huonoa heittoa varten, ja yliviivaa mieluummin vaikea kohta (esim. Täysosuma) kuin arvokas.',
          ],
        },
      ],
    } satisfies Record<AnyGameType, { heading: string; items: string[] }[]>,
    commonMulti: {
      heading: 'Yhteistä moninpeleille',
      items: [
        'Avaa pelipöytä (Pelipöydät-sivulla) tai liity aulassa näkyvään alkamassa olevaan peliin. Avaaja päättää pelaajamäärän, voi lisätä tietokonepelaajia ja aloittaa pelin – myös yksin harjoituksena.',
        'Vuoroaika näkyy palkkina ja sen jälkeen oma aikapankkisi. Jos aika loppuu, peli tekee valinnan puolestasi; kahden peräkkäisen aikakatkaisun jälkeen poistut pelistä.',
        'Pelistä voi poistua kesken – peli jatkuu muille, ja poistuja sijoittuu viimeiseksi.',
        'Pelin aikana voit keskustella muiden pelaajien kanssa pelin chatissa.',
      ],
    },
    common: {
      heading: 'Yhteistä kaikille peleille',
      items: [
        'Haasta aulassa vapaa pelaaja tai valitse satunnainen vastustaja. Haaste on voimassa minuutin, ja sen voi perua. Laivanupotusta ja Othelloa voi pelata myös tietokonetta vastaan.',
        'Vuoroaika näkyy palkkina. Kun se loppuu, oma aikapankkisi alkaa kulua (sekunnit näkyvät ja kuuluu piippaus). Jos aikapankki loppuu, häviät pelin.',
        'Jos yhteys katkeaa, peli ei pysähdy – palaa sivulle, niin pääset jatkamaan samasta kohdasta.',
        'Luovuttaminen päättää pelin, ja vastustaja voittaa.',
        'Pelin aikana voit keskustella vastustajan kanssa pelin chatissa (enintään 160 merkkiä, ei linkkejä).',
      ],
    },
    commonRealtime: {
      heading: 'Yhteistä kaksinpeleille',
      items: [
        'Haasta aulassa vapaa pelaaja tai valitse satunnainen vastustaja. Haaste on voimassa minuutin, ja sen voi perua. Voit pelata myös tietokonetta vastaan.',
        'Luovuttaminen päättää pelin, ja vastustaja voittaa.',
        'Pelin aikana voit keskustella vastustajan kanssa pelin chatissa (enintään 160 merkkiä, ei linkkejä).',
      ],
    },
  },

  // Game tables and multiplayer games (decision 2026-09-27).
  tables: {
    title: 'Pelipöydät',
    intro: 'Avaa pöytä useamman pelaajan peliin tai liity avoimeen pöytään. Peli alkaa, kun avaaja käynnistää sen tai pöytä täyttyy.',
    open: 'Avaa pöytä',
    game: 'Peli',
    maxPlayers: 'Pelaajia enintään',
    countdown: 'Automaattinen alku',
    countdownNone: 'Avaaja käynnistää',
    countdownMin: (n: number) => `${n} min kuluttua`,
    turnTime: 'Miettimisaika per vuoro',
    turnTimeOption: (s: number) => (s < 60 ? `${s} s` : s % 60 === 0 ? `${s / 60} min` : `${Math.floor(s / 60)} min ${s % 60} s`),
    perTurn: (s: number) => `${s} s / vuoro`,
    turnTimeNote: (s: number) => `Miettimisaika ${s} s per vuoro (lisäksi aikapankki).`,
    openTables: 'Avoimet pöydät',
    none: 'Avoimia pöytiä ei ole. Avaa oma pöytä!',
    join: 'Liity',
    full: 'Täynnä',
    seats: (n: number, max: number) => `${n}/${max}`,
    hostedBy: (name: string) => `Avaaja: ${name}`,
    startsIn: (left: string) => `alkaa ${left}`,
    expiresIn: (left: string) => `poistuu ${left}`,
    expiresNote: (left: string) => `Pöytä poistuu ${left} kuluttua, jos peli ei ala.`,
    room: (game: string) => `Pelipöytä – ${game}`,
    host: 'avaaja',
    computer: 'tietokone',
    emptySeat: 'Vapaa paikka',
    addComputer: '＋ Tietokonepelaaja',
    remove: 'Poista',
    start: 'Aloita peli',
    startAlone: 'Pelaa yksin',
    leave: 'Poistu pöydästä',
    waitingHost: (name: string) => `Odotetaan, että ${name} aloittaa pelin.`,
    autoStart: (left: string) => `Peli alkaa automaattisesti ${left} kuluttua, jos pelaajia on vähintään kaksi.`,
    fullStarts: 'Peli alkaa heti, kun pöytä on täynnä.',
    soloNote: 'Yksin pelattu peli on harjoitus: se ei vaikuta voittoihin, mutta paras tulos tallentuu.',
    chat: 'Pöydän chat',
    chatPlaceholder: 'Kirjoita pöytään…',
    closed: {
      cancelled: 'Pelipöytä suljettiin.',
      expired: 'Pelipöytä poistui, koska peli ei alkanut 10 minuutissa.',
      removed: 'Sinut poistettiin pelipöydästä.',
      started: 'Peli alkaa!',
    } satisfies Record<TableEndReason, string>,
    starting: 'Alkamassa',
    lobbyStrip: (game: string, host: string) => `${game} · ${host}`,
    navBadge: (n: number) => `${n} avoinna`,
  },

  multi: {
    turnOf: (name: string) => `${name} pelaa`,
    left: 'poistui',
    offline: 'ei yhteyttä',
    leave: 'Poistu pelistä',
    leaveTitle: 'Poistutaanko pelistä?',
    leaveText: 'Peli jatkuu muille. Sijoitut viimeiseksi, ja poistuminen lasketaan luovutukseksi.',
    resultTitle: 'Tulokset',
    youLeft: 'Poistuit pelistä.',
    placement: (n: number) => `${n}.`,
    points: (n: number) => `${n} p`,
    yourPlacement: (n: number, of: number) => `Sijoituit ${n}./${of}`,
    reason: {
      game_complete: 'Peli päättyi – kaikki kohdat on täytetty.',
      players_left: 'Peli päättyi, koska muut pelaajat poistuivat.',
      aborted: 'Peli keskeytettiin. Peliä ei lasketa tilastoihin.',
    } satisfies Record<MultiEndReason, string>,
    timeoutNote: 'Jos aikasi loppuu, peli valitsee puolestasi. Kahden peräkkäisen aikakatkaisun jälkeen poistut pelistä.',
  },

  dice: {
    roll: 'Heitä',
    rollFirst: 'Heitä nopat',
    rollsLeft: (n: number) => (n === 1 ? '1 heitto jäljellä' : `${n} heittoa jäljellä`),
    noRolls: 'Valitse kohta taulukosta',
    holdHint: 'Napauta noppaa pitääksesi sen seuraavassa heitossa.',
    held: 'pidetty',
    chooseHint: 'Valitse kohta, johon kirjaat pisteet.',
    waitTurn: (name: string) => `${name} heittää…`,
    scored: (name: string, category: string, points: number) => `${name}: ${category} ${points} p`,
    category: 'Kohta',
    upperSum: 'Välisumma',
    bonus: 'Bonus (63+)',
    total: 'Yhteensä',
    scoreConfirm: (category: string, points: number) => `Kirjaa ${category}: ${points} p`,
    zeroConfirm: (category: string) => `Yliviivataanko ${category}? Saat 0 pistettä.`,
    categories: {
      ones: 'Ykköset',
      twos: 'Kakkoset',
      threes: 'Kolmoset',
      fours: 'Neloset',
      fives: 'Viitoset',
      sixes: 'Kuutoset',
      pair: 'Pari',
      twoPairs: 'Kaksi paria',
      threeKind: 'Kolme samaa',
      fourKind: 'Neljä samaa',
      smallStraight: 'Pieni suora',
      largeStraight: 'Iso suora',
      fullHouse: 'Täyskäsi',
      chance: 'Sattuma',
      jackpot: 'Täysosuma',
    } satisfies Record<Category, string>,
    die: (n: number, value: number, held: boolean) => `Noppa ${n}: ${value}${held ? ', pidetty' : ''}`,
  },

  // Private messages page and the unread notice at login (decision 2026-09-29).
  messages: {
    title: 'Yksityisviestit',
    newMessage: 'Uusi viesti',
    cancel: 'Peruuta',
    searchPlaceholder: 'Hae pelaajaa nimellä…',
    noMatches: 'Ei pelaajia tällä nimellä.',
    empty: 'Ei keskusteluja. Aloita valitsemalla "Uusi viesti".',
    choose: 'Valitse keskustelu tai aloita uusi viesti.',
    back: 'Keskustelut',
    retention: 'Viestit säilyvät 30 päivää. Poissa olevalle pelaajalle voi lähettää viestin – hän näkee sen kirjautuessaan.',
    loginNotice: (n: number) => (n === 1 ? 'Sinulle on 1 uusi yksityisviesti' : `Sinulle on ${n} uutta yksityisviestiä`),
    loginFrom: (names: string) => `lähettäjä: ${names}`,
    read: 'Lue viestit',
    dismiss: 'Sulje ilmoitus',
  },

  // Radio: Icecast stream for players with a music permission (decision 2026-09-29).
  // Bingo in the lobby (user request 2026-09-30).
  // The bingo host's page (2026-10-01).
  bingoHost: {
    title: 'Bingoisäntä',
    info: 'Ylläpitäjä on antanut sinulle bingoisännän oikeuden: voit luoda kuponkieriä, juontaa tai käynnistää automaattisen arvonnan ja muuttaa kaikkia bingon asetuksia. Ylläpitäjä voi poistaa oikeuden milloin tahansa.',
    revokedTitle: 'Bingoisännän oikeus on poistettu',
    revokedText: 'Ylläpitäjä on poistanut bingoisännän oikeutesi. Käynnissä oleva automaattinen arvonta jatkuu normaalisti.',
    back: 'Takaisin Areenaan',
  },

  bingoLobby: {
    next: (when: string) => `Seuraava bingoarvonta ${when}`,
    hint: 'Hae oma kuponkisi bingokoodilla ennen arvontaa.',
    get: 'Hae bingokuponki',
    live: 'Bingo on käynnissä nyt!',
    liveHint: 'Seuraa arvontaa ja merkitse numerot kuponkiisi.',
    follow: 'Seuraa arvontaa',
  },

  radio: {
    on: (name: string) => `${name} päälle`,
    off: (name: string) => `${name} pois`,
    menu: (name: string) => `${name}: soitto ja voimakkuus`,
    play: 'Kuuntele',
    stop: 'Pysäytä',
    volume: 'Voimakkuus',
    status: {
      off: 'Pois päältä',
      waiting: 'Napauta Kuuntele tai mitä tahansa sivulla, niin radio alkaa soida.',
      connecting: 'Yhdistetään…',
      playing: 'Soi nyt',
      offline: 'Radio ei ole nyt lähetyksessä – yritetään uudelleen minuutin välein.',
      popup: 'Soi erillisessä radioikkunassa. Kun suljet sen, radio jatkuu tässä.',
    },
    iosVolume: 'iPhonessa ja iPadissa voimakkuutta säädetään laitteen omilla napeilla.',
    mute: 'Mykistä',
    unmute: 'Äänet päälle',
    mutedStatus: 'Mykistetty – lähetys jatkuu',
    popup: 'Avaa pieneen ikkunaan',
    popupTitle: 'Radio omaan pieneen ikkunaan – kuuntelu jatkuu, vaikka suljet Areenan',
  },

  lobbyChat: {
    title: 'Chat',
    lobbyTab: 'Aula',
    placeholder: 'Kirjoita aulaan…',
    pmPlaceholder: (name: string) => `Yksityisviesti pelaajalle ${name}…`,
    send: 'Lähetä',
    empty: 'Ei viestejä. Aloita keskustelu!',
    pmEmpty: 'Ei viestejä vielä.',
    closed: 'Aulan chat on suljettu toistaiseksi. Yksityisviestit toimivat.',
    closeChat: 'Sulje aulan chat',
    openChat: 'Avaa aulan chat',
    delete: 'Poista viesti',
    privacy: 'Huom: ylläpitäjä voi lukea myös yksityisviestit. Aulan viestit ja yksityisviestit säilyvät 30 päivää.',
    message: 'Viesti',
    you: 'Sinä',
    offline: (name: string) => `${name} ei ole nyt paikalla – hän näkee viestisi, kun kirjautuu seuraavan kerran.`,
    allMessages: 'Kaikki viestit →',
    newPm: (name: string) => `Uusi yksityisviesti: ${name}`,
    closeTab: 'Sulje keskustelu',
  },

  chatUi: {
    emojis: 'Hymiöt',
    unknownRecipient: 'Pelaajaa ei löydy – tarkista nimimerkki.',
    emptyPm: 'Kirjoita viesti nimimerkin perään.',
    privateTag: 'yksityinen',
    toYou: 'sinulle',
    clickToPm: (name: string) => `Lähetä yksityisviesti pelaajalle ${name}`,
    sendPm: 'Lähetä yksityisesti',
    pmHint: 'Yksityisviesti: napauta ✉ Yksityisviesti, kirjoita @ ja nimen alku tai napauta nimimerkkiä.',
    newPm: 'Yksityisviesti',
    newPmTitle: 'Lähetä yksityisviesti kenelle tahansa pelaajalle, myös poissa olevalle',
    suggestions: 'Pelaajat',
    notOnline: 'ei paikalla',
  },

  restrictions: {
    chatMuted: (until: string | null) => (until ? `Sinulla on chat-esto ${until} asti.` : 'Sinulla on pysyvä chat-esto.'),
    gameBanned: (until: string) => `Sinulla on pelikielto ${until} asti. Et voi haastaa etkä pelata.`,
  },

  moderation: {
    title: 'Moderointi',
    chatTitle: 'Aulan chat',
    chatOpen: 'Chat on auki.',
    chatClosed: 'Chat on suljettu (vain luku).',
    clearChat: 'Tyhjennä aulan chat',
    clearConfirmTitle: 'Tyhjennetäänkö aulan chat?',
    clearConfirmText: 'Kaikki aulan viestit poistetaan pysyvästi.',
    cleared: (n: number) => `Aulan chat tyhjennetty (${n} viestiä).`,
    restrictTitle: 'Estot',
    player: 'Pelaaja',
    choosePlayer: 'Valitse pelaaja…',
    type: 'Esto',
    types: { chat_mute: 'Chat-esto', game_ban: 'Pelikielto' },
    duration: 'Kesto',
    durations: { 60: '1 tunti', 1440: '24 tuntia', 10080: '7 päivää', 43200: '30 päivää' } as Record<number, string>,
    permanent: 'Pysyvä',
    reason: 'Syy (valinnainen)',
    add: 'Aseta esto',
    added: 'Esto asetettu.',
    active: 'Voimassa olevat estot',
    none: 'Ei voimassa olevia estoja.',
    until: (s: string) => `${s} asti`,
    forever: 'pysyvä',
    by: (name: string) => `asettanut ${name}`,
    deletedUser: 'poistettu käyttäjä',
    revoke: 'Poista esto',
    revoked: 'Esto poistettu.',
    logTitle: 'Moderointiloki',
    logEmpty: 'Ei merkintöjä.',
    actions: {
      chat_mute: 'chat-esto',
      game_ban: 'pelikielto',
      revoke_chat_mute: 'chat-esto poistettu',
      revoke_game_ban: 'pelikielto poistettu',
      delete_message: 'viesti poistettu',
      open_lobby_chat: 'chat avattu',
      close_lobby_chat: 'chat suljettu',
      clear_lobby_chat: 'chat tyhjennetty',
      delete_user: 'tunnus poistettu',
    } as Record<string, string>,
  },

  sound: {
    title: 'Äänet',
    enabled: 'Ääniefektit päällä',
    volume: 'Äänenvoimakkuus',
    test: 'Testaa ääntä',
    info: 'Asetus tallentuu tähän selaimeen. iPadilla ja iPhonella tarkista myös, ettei laite ole äänettömässä tilassa.',
    status: 'Äänen tila',
    session: 'äänisessio',
    states: {
      running: 'toiminnassa',
      suspended: 'odottaa kosketusta',
      closed: 'suljettu',
      interrupted: 'keskeytetty',
      none: 'ei vielä käynnistetty',
      unsupported: 'ei tuettu tässä selaimessa',
    } as Record<string, string>,
    mute: 'Mykistä äänet',
    unmute: 'Äänet päälle',
    chatSounds: 'Chatin äänet',
    pmNotifications: 'Yksityisviesti-ilmoitukset pelin aikana',
    pmNotificationsHint: 'Näyttää viestin alun ja soittaa äänimerkin, kun saat yksityisviestin kesken pelin.',
    chatMute: 'Chatin äänet pois',
    chatUnmute: 'Chatin äänet päälle',
  },

  stats: {
    title: 'Tilastot',
    played: 'Pelatut',
    wins: 'Voitot',
    losses: 'Tappiot',
    resignations: 'Luovutukset',
    timeouts: 'Aikaylitykset',
    draws: 'Tasapelit',
    winPct: 'Voitto-%',
    level: 'Kokemustaso',
    nextLevel: (n: number) => `Seuraavaan tasoon: ${n} ${n === 1 ? 'peli' : 'peliä'}`,
    maxLevel: 'Korkein taso saavutettu!',
    none: 'Ei vielä pelattuja pelejä.',
    bestScore: (game: string, points: number) => `${game}: paras tulos ${points} pistettä`,
  },

  status: {
    realtime: 'Reaaliaikayhteys',
    connected: 'Yhdistetty',
    connecting: 'Yhdistetään…',
    disconnected: 'Ei yhteyttä',
    latency: 'Viive',
    serverTime: 'Palvelimen aika',
    connectionLost: 'Yhteys palvelimeen katkesi. Yhdistetään uudelleen…',
  },

  account: {
    title: 'Oma tili',
    username: 'Käyttäjätunnus',
    displayName: 'Näyttönimi',
    role: 'Rooli',
    changePassword: 'Vaihda salasana',
    currentPassword: 'Nykyinen salasana',
    newPassword: 'Uusi salasana',
    repeatPassword: 'Uusi salasana uudelleen',
    save: 'Vaihda salasana',
    saved: 'Salasana vaihdettu. Muut kirjautumiset on kirjattu ulos.',
    mismatch: 'Salasanat eivät täsmää.',
    currentWrong: 'Nykyinen salasana on väärä.',
  },

  admin: {
    title: 'Käyttäjät',
    newUser: 'Uusi käyttäjä',
    newUserInfo:
      'Anna käyttäjätunnus ja luo liittymislinkki. Pelaaja valitsee linkin kautta itse pelaajanimen ja salasanan. Linkki toimii kerran ja vanhenee 2 tunnin kuluttua.',
    username: 'Käyttäjätunnus',
    displayName: 'Näyttönimi',
    role: 'Rooli',
    createLink: 'Luo liittymislinkki',
    linkCreated: (username: string) => `Liittymislinkki tunnukselle ${username}`,
    resetCreated: (name: string) => `Salasanan vaihtolinkki: ${name}`,
    linkOnce: 'Linkki näytetään vain nyt. Kopioi se ja lähetä pelaajalle.',
    resetOnce: 'Linkki näytetään vain nyt. Kopioi se ja lähetä pelaajalle. Vanha salasana toimii, kunnes linkkiä käytetään.',
    linkExpires: (when: string) => `Vanhenee ${when}.`,
    copy: 'Kopioi',
    copied: 'Kopioitu!',
    copyFailed: 'Kopiointi ei onnistunut – valitse teksti ja kopioi se itse.',
    close: 'Sulje',
    linksTitle: 'Voimassa olevat linkit',
    linkPurpose: { join: 'Liittymislinkki', reset: 'Salasanan vaihto' },
    cancelLink: 'Mitätöi',
    joinMethod: { admin: 'YP', link: 'YP-linkki', code: 'Kutsukoodi', guest: 'Vieras' },
    joinMethodTitle: 'Liittymistapa: YP = ylläpitäjän lisäämä',
    joined: 'Liittyi',
    status: 'Tila',
    lastLogin: 'Viimeksi',
    never: 'Ei koskaan',
    active: 'Aktiivinen',
    locked: 'Lukittu',
    lock: 'Lukitse',
    unlock: 'Avaa lukitus',
    makeAdmin: 'Tee ylläpitäjäksi',
    makePlayer: 'Tee pelaajaksi',
    resetPassword: 'Salasanan vaihtolinkki',
    delete: 'Poista',
    deleteTitle: (name: string) => `Poistetaanko käyttäjä ${name}?`,
    deleteInfo:
      'Tunnus poistetaan pysyvästi: kaikki pelaajan pelit (myös vastustajien otteluhistoriasta), viestit, tilastot ja estot. Vastustajien kokonaistilastot säilyvät. Poistoa ei voi perua.',
    deleteConfirm: 'Poista pysyvästi',
    deleted: (name: string) => `Käyttäjä ${name} poistettu.`,
    save: 'Tallenna',
    cancel: 'Peruuta',
    you: 'sinä',
    hints: {
      username: '3–32 merkkiä: a–z, 0–9, _ . -',
    },
    tabs: {
      users: 'Käyttäjät',
      invites: 'Kutsukoodit',
      settings: 'Asetukset',
      radio: 'Musiikki',
      bingo: 'Bingo',
      sms: 'SMS',
      coins: 'AreCoin',
      games: 'Pelit',
      messages: 'Yksityisviestit',
      closure: 'Huoltotila',
    },
    invites: {
      registrationTitle: 'Rekisteröityminen kutsukoodilla',
      registrationOpen: 'Sallittu: uudet pelaajat voivat rekisteröityä aktiivisten erien koodeilla. (Ei koske vieraskoodeja.)',
      registrationClosed: 'Estetty: mikään kutsukoodi ei toimi, ennen kuin sallit rekisteröitymisen.',
      allow: 'Salli',
      deny: 'Estä',
      newTitle: 'Luo kutsukoodeja',
      newInfo:
        'Koodit ovat voimassa 3 kuukautta luomisesta. Erän tunnisteesta (esim. erän numero) näet, minkä erän koodilla pelaaja liittyi.',
      kind: 'Koodien tyyppi',
      kindRegister: 'Kutsukoodit – rekisteröityminen omalla tunnuksella (voimassa 3 kk)',
      kindGuest: 'Vieraskoodit – kertakäyttöinen kokeilu vieraana, enintään 3 h (voimassa 1 viikko)',
      newInfoGuest:
        'Vieraskoodit ovat voimassa viikon luomisesta. Jokaisella koodilla pääsee kerran vieraaksi enintään 3 tunniksi: leikkinimi, ei chattiin kirjoittamista, pelejä ei tilastoida ja vierailun tiedot poistetaan sen päätyttyä.',
      guestBadge: 'Vieraskoodit',
      guestVisited: (when: string) => `vierailu ${when}`,
      label: 'Erän tunniste',
      count: 'Määrä (1–200)',
      create: 'Luo koodit',
      created: (label: string, n: number) => `Erä ${label}: ${n} koodia luotu.`,
      batchesTitle: 'Kutsukoodierät',
      none: 'Ei kutsukoodieriä.',
      status: { active: 'Aktiivinen', paused: 'Keskeytetty', expired: 'Vanhentunut' },
      used: (used: number, total: number) => `${used}/${total} käytetty`,
      validUntil: (when: string) => `voimassa ${when} asti`,
      expiredOn: (when: string) => `vanheni ${when}`,
      createdBy: (name: string) => `luonut ${name}`,
      showCodes: 'Näytä koodit',
      hideCodes: 'Piilota koodit',
      copyUnused: 'Kopioi käyttämättömät',
      copiedN: (n: number) => `${n} koodia kopioitu leikepöydälle.`,
      noUnused: 'Erässä ei ole käyttämättömiä koodeja.',
      print: 'Tulosta',
      pause: 'Keskeytä',
      resume: 'Ota käyttöön',
      revoke: 'Mitätöi',
      pauseInfo: 'Keskeytetyn erän koodit eivät toimi, ennen kuin otat erän uudelleen käyttöön.',
      revokeTitle: (label: string) => `Mitätöidäänkö erä ${label}?`,
      remove: 'Poista',
      removeTitle: (label: string) => `Poistetaanko vanhentunut erä ${label}?`,
      revokeText:
        'Erä ja sen koodit poistetaan pysyvästi, eivätkä käyttämättömät koodit enää toimi. Koodeilla jo rekisteröityneet pelaajat säilyvät, ja heidän tiedoissaan näkyy edelleen erän tunniste. Mitätöintiä ei voi perua.',
      revoked: (label: string) => `Erä ${label} mitätöity ja poistettu.`,
      unused: 'käyttämätön',
      usedBy: (who: string, when: string) => `${who} · ${when}`,
      usedByDeleted: (when: string) => `poistettu käyttäjä · ${when}`,
      printTitle: (label: string) => `Tulosta kutsukortit – ${label}`,
      printQr: 'QR-koodi kortteihin (vie suoraan rekisteröitymiseen koodi valmiiksi täytettynä)',
      printOnlyUnused: 'Vain käyttämättömät koodit',
      printCount: (n: number) => `${n} korttia, ${Math.ceil(n / 8)} A4-arkkia`,
      printHint: 'Tulosta värillisenä ja ilman sivun reunuksia (otsake/alatunniste pois). Leikkaa katkoviivoja pitkin. Tulostusikkunasta voit myös tallentaa PDF:ksi.',
      printNow: 'Tulosta',
      save: 'Tallenna',
      saveTitle: (label: string) => `Tallenna kutsukortit – ${label}`,
      saveNow: 'Tallenna',
      format: 'Tiedostomuoto',
      formatPdf: 'PDF – A4-arkit tulostamista varten (8 korttia arkille)',
      formatJpgSheets: 'JPG – kortit arkkeina (enintään 8 korttia kuvaa kohden)',
      formatJpgCards: 'JPG – jokainen kortti omana kuvanaan (sähköpostiin tai WhatsAppiin)',
      saveCount: (cards: number, files: number) => `${cards} korttia, ${files} ${files === 1 ? 'kuva' : 'kuvaa'}`,
      saving: 'Tallennetaan…',
      saved: (files: number) => (files === 1 ? 'Tiedosto tallennettu latauksiin.' : `${files} tiedostoa tallennettu latauksiin.`),
      saveFailed: 'Tallennus ei onnistunut. Kokeile toisella selaimella tai tulosta ja valitse tulostimeksi "Tallenna PDF-muodossa".',
      saveHint:
        'Tiedostot tallentuvat selaimen latauskansioon. Jos kuvia on useita, selain voi kysyä luvan usean tiedoston lataamiseen.',
      card: {
        heading: 'Kutsu Areenaan',
        code: 'Kutsukoodi',
        register: 'Rekisteröidy osoitteessa',
        scan: 'tai lue QR-koodi puhelimella',
        valid: (when: string) => `Voimassa ${when} asti`,
      },
      // Guest code cards (decision 2026-09-29).
      guestCard: {
        heading: 'Kokeile Areenaa',
        code: 'Vieraskoodi',
        register: 'Pelaa vieraana osoitteessa',
        scan: 'tai lue QR-koodi puhelimella',
        valid: (when: string) => `Voimassa ${when} asti`,
        slogan: 'Yksi vierailu, enintään 3 tuntia – pelaa heti ilman tunnusta!',
      },
      slogans: [
        'Haasta kaverisi – ja voita!',
        'Upota, käännä, muista ja heitä!',
        'Kaverit odottavat jo aulassa.',
        'Peli alkaa, kun sinä liityt.',
        'Areenalla jokainen siirto ratkaisee.',
        'Parhaat pelit pelataan yhdessä.',
        'Laivanupotusta, Othelloa, muistipeliä ja Nopparallia.',
        'Tule mukaan – pelisali on auki!',
      ],
    },
    mustChange: 'vaihtaa salasanan',
    musicBadge: 'musiikkilupa',
    musicAlways: 'musiikki aina (ylläpitäjä)',
    musicAllow: 'Anna musiikkilupa',
    musicRevoke: 'Poista musiikkilupa',
    musicTitle: 'Musiikkilupa: pelaaja kuulee radion (Ylläpito → Musiikki)',
    bingoHostGrant: 'Anna bingoisännän oikeus',
    bingoHostRevoke: 'Poista bingoisännän oikeus',
    bingoHostBadge: 'bingoisäntä',
    bingoHostTitle: 'Bingoisäntä saa Bingo-sivun: kuponkierät, arvonnat ja kaikki bingon asetukset. Oikeuden voi poistaa milloin tahansa, myös kesken arvonnan.',
    // Codes by text message (user decisions 2026-10-01).
    sms: {
      formTitle: 'Kuponkien jakelun asetukset',
      formInfo:
        'Julkinen lomake (nimimerkki, 1–3 koodia, puhelinnumero tai sähköposti, bottitarkistus), jolla pyydetään bingokoodit tekstiviestinä tai sähköpostina. Nimimerkki varataan heti, ja lähetetyt koodit varataan sille: ne aktivoituvat, kun pelaaja syöttää jonkin niistä bingosivulla. Samalle numerolle tai osoitteelle yhteensä enintään 3 koodia, samasta verkosta (IP) enintään 3 vuorokaudessa – raja alkaa alusta, kun lomake avataan uudelleen. Lomake sulkeutuu itsestään, kun enimmäismäärä täyttyy. Kun lomake suljetaan, ilmoittautumiset jäävät alle käsiteltäviksi. Kaikki asetukset tallentuvat heti: kytkimet ja valinnat muutettaessa, numero- ja tekstikentät kentästä poistuttaessa.',
      botMissing: 'Bottitarkistuksen (Cloudflare Turnstile) avaimet puuttuvat palvelimelta – lomake pysyy suljettuna, vaikka se kytketään päälle.',
      botTest: 'Bottitarkistus on testitilassa (Cloudflaren testiavaimet päästävät kaikki läpi). Vaihda omat avaimet ennen kuin lomake avataan julkisesti.',
      open: 'Ilmoittautumislomake auki',
      openHint: 'Lomake näkyy myös bingosivulla linkkinä. Suljettuna ilmoittautumisia ei oteta vastaan lomakkeelta eikä tekstiviestillä.',
      ipLock: 'IP-esto: samasta verkosta vain yksi nimimerkki',
      ipLockHint:
        'Lomakkeella ei voi ilmoittautua toisella nimimerkillä samasta verkosta (IP-osoitteesta), kun lomake on auki; saman nimimerkin lisäkoodit sallitaan. Huom: saman kodin, työpaikan tai mobiiliverkon käyttäjillä voi olla sama IP-osoite. Ei koske tekstiviesti-ilmoittautumisia.',
      smsInstant: 'Tekstiviestikoodit lähtevät heti',
      emailInstant: 'Sähköpostikoodit lähtevät heti',
      instantHint: 'Päällä: koodit lähtevät heti välittömän lähetyksen erästä. Pois: ilmoittautuminen jää listaan, ja koodit lähtevät Lähetä koodit -painikkeella.',
      playerMaxHint: 'Kuponkeja yhdestä pelistä pelaajaa kohden. Pelaajakohtaisen rajan voi asettaa Koodit ja pelaajat -välilehdellä.',
      saved: 'Tallennettu.',
      invalidField: 'Arvo ei kelpaa – kenttä palautettiin. Viestipohjissa pitää olla {koodi}.',
      count: (n: number, max: number) => `Pyydettyjä koodeja ${n} / ${max}`,
      maxCodes: 'Koodeja jaetaan enintään (kaikki ilmoittautumiset yhteensä)',
      info: 'Lisätieto lomakkeella (valinnainen)',
      infoPlaceholder: 'esim. Seuraava arvonta pe 3.10. klo 20',
      template: 'Viestipohja',
      templateHint: '{koodi} = ilmoittautujan koodit, {nimi} = nimimerkki. Ei linkkejä. Pidä viesti lyhyenä (yksi tekstiviesti on 160 merkkiä; ä ja ö lyhentävät sen 70 merkkiin).',
      preview: 'Esimerkki',
      formLink: 'Lomakkeen osoite (jaa somessa tai lähetyksessä)',
      formOpened: 'Ilmoittautumislomake on nyt auki.',
      formClosed: 'Ilmoittautumislomake on nyt suljettu.',
      smsSignup: 'Ilmoittautuminen myös tekstiviestillä',
      smsSignupInfo:
        'Pelaaja lähettää puhelimen numeroon viestin BINGO 3 Nimimerkki (1–3 koodia, ilman määrää 3). Ilmoittautuminen toimii kuten lomake ja vain lomakkeen ollessa auki; koodit lähtevät heti tai manuaalisesti kuten lomakkeelta. Manuaalisessa lähetyksessä pelaaja saa kuittauksen. Virheelliseen viestiin vastataan enintään 3 kertaa vuorokaudessa numeroa kohden. Vain suomalaiset numerot. Pois päältä: viestejä ei lueta eikä mitään lähetetä, eikä pois päältä olon aikana tulleita viestejä käsitellä myöhemmin.',
      smsSignupOn: 'Ilmoittautuminen tekstiviestillä on päällä.',
      smsSignupOff: 'Ilmoittautuminen tekstiviestillä on pois päältä.',
      smsInboxOk: (when: string | null) => `Puhelimen viestit luettu viimeksi ${when ?? '–'}.`,
      smsInboxError: (error: string) => `Puhelimen viestejä ei saatu luettua: ${error}.`,
      viaSms: 'tekstiviestillä',
      listTitle: (n: number) => `Ilmoittautuneet (${n})`,
      listInfo:
        'Valitse vastaanottajat (tai Valitse kaikki), kirjoita kuponkierän nimi ja paina Lähetä koodit. Jos erää ei ole, se luodaan tarvittavalla koodimäärällä. Sähköpostit lähtevät heti, tekstiviestit puhelimesta muutaman sekunnin välein; tila päivittyy (lähetetty → perillä). Epäonnistuneet voi valita ja lähettää uudelleen samalla koodilla. WhatsApp-painike avaa valmiin viestin varalle.',
      none: 'Ei vielä ilmoittautuneita.',
      selectAll: (n: number) => `Valitse kaikki lähetettävät (${n})`,
      selectNone: 'Tyhjennä valinnat',
      colName: 'Nimimerkki',
      codesAsked: (n: number) => (n === 1 ? '1 koodi' : `${n} koodia`),
      colPhone: 'Yhteystieto',
      colTime: 'Ilmoittautui',
      colState: 'Tila',
      colCode: 'Koodit',
      status: { pending: 'odottaa', sending: 'lähetetään', sent: 'lähetetty', delivered: 'perillä', failed: 'epäonnistui' } as Record<string, string>,
      whatsappTitle: 'Avaa WhatsApp valmiilla viestillä (sinä painat Lähetä)',
      batchLabel: 'Kuponkierän nimi',
      batchPlaceholder: 'esim. Mobiili pe 3.10.',
      labelMissing: 'Kirjoita kuponkierän nimi.',
      send: (n: number) => `Lähetä koodit (${n})`,
      sending: (n: number) => `Lähetys aloitettu: ${n} viestiä.`,
      progress: (done: number, total: number) => `Lähetetään… ${done} / ${total}`,
      confirmTitle: (n: number) => `Lähetetäänkö ${n} viestiä?`,
      confirmSend: 'Lähetä',
      confirmText: (sms: number, emails: number, needed: number, label: string) =>
        `${sms} tekstiviestiä lähtee puhelimestasi ja ${emails} sähköpostia bingon postilaatikosta. Uusia koodeja tarvitaan ${needed} erästä "${label}" (epäonnistuneet saavat saman koodin uudelleen).`,
      useExisting: (free: number) => `Erä on jo olemassa: vapaita koodeja ${free}.`,
      notEnough: (free: number, needed: number) => `Erässä on vain ${free} vapaata koodia, tarvitaan ${needed}. Valitse toinen erän nimi tai vähemmän vastaanottajia.`,
      willCreate: (sizes: string) => `Erää ei ole – se luodaan (${sizes} kuponkia; erässä enintään 50, tarvittaessa lisäerät "nimi 2", "nimi 3" …).`,
      registrationClosed: 'Huom: erän kuponkien haku on suljettu – vastaanottajat eivät voi noutaa kuponkeja ennen kuin avaat sen.',
      removeSelected: (n: number) => `Poista valitut (${n})`,
      removeAll: 'Tyhjennä ilmoittautumiset',
      removeSelectedTitle: (n: number) => `Poistetaanko ${n} ilmoittautumista?`,
      removeAllTitle: 'Tyhjennetäänkö kaikki ilmoittautumiset?',
      removeText: 'Nimimerkit, puhelinnumerot ja osoitteet poistetaan listalta pysyvästi. Jo lähetetyt koodit toimivat edelleen, ja varatut nimimerkit säilyvät pelaajina. (Tiedot poistuvat muutenkin automaattisesti 30 päivän kuluttua.)',
      removed: 'Ilmoittautumiset poistettu.',
      phoneTitle: 'Puhelin (SMS Gateway for Android)',
      phoneInfo:
        'Viestit lähtevät Android-puhelimestasi sovelluksella SMS Gateway for Android (lähiverkkotila). Kirjoita sovelluksen Local Server -kohdassa näkyvä osoite (http://puhelimen-osoite:8080), käyttäjätunnus ja salasana. Puhelimen on oltava kotiverkossa ja sovelluksen käynnissä lähetyksen aikana.',
      url: 'Puhelimen osoite',
      username: 'Käyttäjätunnus',
      password: 'Salasana',
      passwordKept: 'tallennettu – kirjoita vain jos vaihdat',
      savePhone: 'Tallenna puhelimen asetukset',
      phoneSaved: 'Puhelimen asetukset tallennettu.',
      testPhone: 'Testiviestin vastaanottaja',
      test: 'Lähetä testiviesti',
      testSent: 'Testiviesti lähti puhelimesta – tarkista, että se tuli perille.',
      testFailed: 'Testiviesti epäonnistui.',
      emailTitle: 'Sähköposti',
      emailOff: 'Sähköpostivalinta näkyy lomakkeella vasta, kun postilaatikon tunnus ja salasana on tallennettu (Lähetysasetukset).',
      instantBatch: 'Välittömän lähetyksen erä',
      instantBatchNone: '– ei valittu –',
      instantBatchHint: 'Heti lähtevät koodit annetaan tästä erästä. Jos koodit loppuvat, ilmoittautuminen jää listaan odottamaan.',
      instantNoBatch: 'Valitse välittömän lähetyksen erä – ilman sitä kaikki koodit lähtevät manuaalisesti.',
      smsInstantNoPhone: 'Puhelimen tunnukset puuttuvat (Lähetysasetukset) – tekstiviestit lähtevät manuaalisesti, kunnes ne on tallennettu.',
      textsTitle: 'Viestien tekstit',
      connectionsTitle: 'Lähetysasetukset (puhelin ja postilaatikko)',
      connectionsState: (phone: boolean, mail: boolean) => `– puhelin ${phone ? 'asetettu' : 'puuttuu'}, sähköposti ${mail ? 'asetettu' : 'puuttuu'}`,
      emailBatch: 'Sähköpostin välitön erä',
      emailBatchNone: '– ei välitöntä lähetystä (sähköpostit lähtevät Lähetä koodit -painikkeella) –',
      emailBatchOption: (label: string, free: number) => `${label} (vapaita ${free})`,
      emailBatchHint:
        'Kun erä on valittu, sähköpostilla ilmoittautuva saa pyytämänsä koodit erästä heti. Jos koodit loppuvat, ilmoittautuminen jää listaan odottamaan.',
      emailSubject: 'Sähköpostin aihe',
      emailTemplate: 'Sähköpostin teksti',
      emailTemplateHint: '{nimi} = nimimerkki, {koodi} = ilmoittautujan koodit. Ei linkkejä.',
      mailTitle: 'Postilaatikko (sähköpostin lähetys)',
      mailInfo:
        'Bingokoodit lähtevät sähköpostina tästä postilaatikosta (SMTP, salattu yhteys portissa 465). Tunnus on postilaatikon osoite, salasana postilaatikon salasana.',
      mailHost: 'Palvelin',
      mailPort: 'Portti',
      mailUser: 'Tunnus (postilaatikko)',
      saveMail: 'Tallenna postilaatikon asetukset',
      mailSaved: 'Postilaatikon asetukset tallennettu.',
      mailTestTo: 'Testiviestin vastaanottaja',
      mailTest: 'Lähetä testisähköposti',
      mailTestSent: 'Testisähköposti lähti – tarkista, että se tuli perille (myös roskaposti).',
    },
    // The ad carousel (user request 2026-10-01).
    carousel: {
      title: 'Mainoskaruselli',
      info: 'Miten sponsorimainokset soivat automaattisessa bingossa. Mainokset kytketään päälle tai pois Automaattinen bingo -välilehdellä. Sama mainos ei koskaan soi kahdesti peräkkäin, ja järjestyksessä soitettaessa vuoro jatkuu viimeksi soitetusta mainoksesta seuraavaan peliin.',
      adsOff: 'Mainokset on nyt kytketty pois päältä (Automaattinen bingo -välilehti).',
      order: 'Soittotapa',
      orders: {
        sequence: 'Järjestyksessä (alla olevan listan järjestys)',
        random: 'Satunnaisesti – samaa ei toisteta ennen kuin kaikki on soitettu',
        weighted: 'Painotettu satunnainen – painoarvo 2 soi kaksi kertaa useammin kuin 1',
      } as Record<string, string>,
      startCount: 'Mainoksia pelin alussa (0–5)',
      endCount: 'Mainoksia pelin lopussa (0–5)',
      breaks: 'Mainostauot arvonnan aikana',
      breakEvery: 'Tauko, kun on arvottu näin monta numeroa (3–75)',
      perBreak: 'Mainoksia per tauko (1–5)',
      maxPerGame: 'Mainoksia enintään per peli (0 = ei rajaa)',
      save: 'Tallenna karusellin asetukset',
      saved: 'Mainoskarusellin asetukset tallennettu.',
      listTitle: 'Mainokset karusellissa',
      listInfo:
        'Karusellissa olevat mainokset soivat tässä järjestyksessä (järjestyssoitossa). Painoarvo vaikuttaa painotettuun soittoon. Voimassaoloaika rajaa mainoksen soimaan vain annettuina päivinä (tyhjä = ei rajaa); vanhentunut mainos jää kierrosta pois itsestään.',
      noneIn: 'Karusellissa ei ole mainoksia – lisää niitä alta.',
      outsideTitle: 'Ei karusellissa',
      up: 'Siirrä ylös',
      down: 'Siirrä alas',
      add: 'Ota karuselliin',
      take: 'Poista karusellista',
      weight: 'Painoarvo',
      from: 'Alkaen',
      until: 'Päättyen',
      expired: 'vanhentunut',
      upcoming: 'ei vielä voimassa',
      meta: (length: string, plays: number) => `${length} · soitettu ${plays} kertaa`,
      adSaved: (name: string) => `Mainoksen ${name} asetukset tallennettu.`,
      orderSaved: 'Järjestys tallennettu.',
      listSaved: 'Karuselli päivitetty.',
      removeText: 'Mainos poistetaan palvelimelta ja karusellista. Sen soitot säilyvät soittotilastossa.',
      statsTitle: 'Soittotilasto',
      statsInfo: 'Kuinka monta kertaa kukin mainos on soitettu valitulla aikavälillä – esimerkiksi raportiksi sponsorille. Poistettujen mainosten soitot näkyvät nimellä.',
      show: 'Näytä',
      csv: 'Lataa CSV',
      csvHead: 'Mainos;Soittoja;Ensimmäinen;Viimeisin',
      noPlays: 'Ei soittoja valitulla aikavälillä.',
      colAd: 'Mainos',
      colPlays: 'Soittoja',
      colFirst: 'Ensimmäinen',
      colLast: 'Viimeisin',
      removed: 'poistettu',
    },
    // Bingo players and the coupon limit (decisions 2026-10-01).
    // The waiting music (user request 2026-10-03).
    bingoMusic: {
      title: 'Odotusajan musiikki',
      info: 'Kun automaattibingon asetuksissa on "Odotusajan musiikki" päällä, nämä kappaleet soivat pelaajien bingosivulla tässä järjestyksessä (tai satunnaisesti, asetuksen mukaan) 30 minuuttia ennen arvontaa alkaen – kaikilla sama kohta, myöhemmin tulleella kesken kappaleen. Lista alkaa alusta, jos se loppuu kesken. Musiikki hiljenee kuulutusten ajaksi ja loppuu, kun aloituskuulutukset alkavat. Äänenvoimakkuudet tasataan latauksessa.',
      none: 'Ei vielä kappaleita.',
      total: (n: number, len: string) => `${n} kappaletta, yhteensä ${len}`,
      upload: 'Lisää kappaleita (MP3, enintään 12 Mt / kappale)',
      uploadHint: 'Valitse yksi tai useampia MP3-tiedostoja, kokonainen kansio tai ZIP-tiedosto (selain purkaa sen; palvelimelle tallentuvat vain kappaleet). Uudet kappaleet tulevat listan loppuun.',
      working: 'Ladataan…',
      added: 'Kappaleet lisätty.',
      tooBig: (name: string) => `${name}: tiedosto on yli 12 Mt.`,
      bad: (name: string) => `${name}: tiedostoa ei voitu lukea MP3:na.`,
      up: 'Siirrä ylemmäs',
      down: 'Siirrä alemmas',
      removeTitle: (name: string) => `Poistetaanko kappale ${name}?`,
      removeText: 'Kappale poistetaan pysyvästi odotusmusiikista.',
      uploadFolder: 'Valitse kansio',
      uploadZip: 'Valitse ZIP-tiedosto',
      progress: (done: number, total: number) => `Ladataan… ${done} / ${total}`,
      zipBad: 'ZIP-tiedostoa ei voitu lukea.',
      noMp3: 'Valinnassa ei ollut MP3-tiedostoja.',
      addedCount: (n: number, failed: number) => `Lisätty ${n} kappaletta${failed > 0 ? `, ${failed} ohitettiin (liian iso tai ei MP3)` : ''}.`,
      streamTitle: 'Audiostriimi',
      streamInfo:
        'Striimin (esim. Icecast) osoite, jota voi käyttää odotusmusiikkina (valinta Automaattinen bingo -välilehdellä). Pelin aikainen taustamusiikki soi aina tiedostosta. Osoitteen on oltava https. Jos striimipalvelin ei salli selaimen äänenkäsittelyä (CORS), striimi soi silti, mutta iPhonessa se ei hiljene kuulutusten ajaksi. Tyhjä = ei striimiä.',
      streamUrl: 'Striimin osoite',
      streamTest: 'Kuuntele',
      streamSave: 'Tallenna striimi',
      streamSaved: 'Striimin osoite tallennettu.',
    },
    // The bingo log (user request 2026-10-02).
    bingoLogs: {
      title: 'Bingolokit',
      info: 'Jokaisesta pelistä tallentuu loki: arvotut numerot, syntyneet bingot (myös huutamattomat ja vanhentuneet), kaikki BINGO!-painallukset (kuka, milloin, tulos) ja voittajat. PDF:ssä on kuponkikuvat jokaisesta bingotilanteesta. Lokit säilyvät, kunnes poistat ne – myös kun kuponkierä poistetaan.',
      none: 'Ei vielä lokeja – loki syntyy, kun seuraava peli alkaa.',
      game: (id: number) => `Peli #${id}`,
      running: 'käynnissä',
      auto: 'automaattinen',
      host: 'juonnettu',
      counts: (coupons: number, players: number, draws: number, wins: number) =>
        `${coupons} kuponkia · ${players} pelaajaa · arvottu ${draws}/75 · voittoja ${wins}`,
      pdf: 'Lataa PDF',
      making: 'Tehdään…',
      removeTitle: (id: number) => `Poistetaanko pelin #${id} loki?`,
      removeText: 'Loki poistetaan pysyvästi. Peliin ja kuponkeihin poisto ei vaikuta.',
      removed: (id: number) => `Pelin #${id} loki poistettu.`,
    },
    bingoPlayers: {
      title: 'Bingopelaajat ja kuponkiraja',
      info: 'Pelaaja syntyy, kun hän noutaa ensimmäisen kuponkinsa tai ilmoittautuu lomakkeella, ja poistuu automaattisesti, kun hänellä ei ole enää koodeja missään erässä (esim. erän poiston jälkeen) eikä odottavaa ilmoittautumista. Nimimerkki on yksilöllinen, ja pelaajan muut kupongit noudetaan samalla nimimerkillä. Raja koskee kuponkeja eristä, joita ei ole vielä pelattu tai joiden haku on auki (eli yhtä peliä). Pelaajalle voi asettaa oman rajan; tyhjä = yleinen raja.',
      max: 'Kuponkeja enintään pelaajaa kohden (yleinen raja)',
      maxElsewhere: (n: number) => `Yleinen raja: ${n} kuponkia pelaajaa kohden (muutetaan Koodien jakelu -välilehden asetuksista).`,
      maxSaved: (n: number) => `Yleinen raja: ${n} kuponkia pelaajaa kohden.`,
      ownSaved: (nick: string) => `Pelaajan ${nick} raja tallennettu.`,
      none: 'Ei vielä pelaajia.',
      search: 'Hae nimimerkillä',
      counts: (upcoming: number, total: number, reserved: number) =>
        `tulevaan peliin ${upcoming} · kaikkiaan ${total} kuponkia${reserved > 0 ? ` · varattuja koodeja ${reserved}` : ''}`,
      lastSeen: (when: string | null) => (when ? `bingosivulla viimeksi ${when}` : 'ei vielä käynyt bingosivulla'),
      rename: 'Vaihda nimimerkki',
      renameTitle: (nick: string) => `Uusi nimimerkki pelaajalle ${nick}`,
      renameLabel: 'Uusi nimimerkki',
      renamed: (nick: string) => `Nimimerkki vaihdettu: ${nick}`,
      codesTitle: (n: number) => `Pelaajien koodit (${n})`,
      codesInfo:
        'Kaikki pelaajille varatut ja käytetyt koodit. Vapauta koodi: koodi otetaan pelaajalta, vanha koodi lakkaa toimimasta ja tilalle tulee uusi koodi, jonka voit antaa toiselle – tai varata heti valitulle pelaajalle. Käynnissä olevan pelin kuponkia ei voi vapauttaa.',
      codesNone: 'Ei vielä pelaajien koodeja.',
      codeReserved: 'varattu (ei aktivoitu)',
      codeCard: (n: number) => `kuponki ${n}`,
      claimingOpen: 'haku auki',
      claimingClosed: 'haku suljettu',
      release: 'Vapauta',
      releaseTitle: (code: string, nick: string) => `Vapautetaanko koodi ${code} pelaajalta ${nick}?`,
      releaseText: 'Vanha koodi lakkaa toimimasta ja pelaaja menettää sen kupongin. Tilalle tulee uusi koodi samaan erään.',
      releaseTo: 'Uusi koodi',
      releaseFree: '– vapaaksi, annan sen itse –',
      releaseConfirm: 'Vapauta koodi',
      released: (code: string) => `Uusi koodi: ${code}`,
      own: 'Oma raja',
      remove: 'Poista',
      removeAll: (n: number) => `Poista kaikki pelaajat (${n})`,
      removeTitle: (nick: string) => `Poistetaanko pelaaja ${nick}?`,
      removeAllTitle: (n: number) => `Poistetaanko kaikki ${n} pelaajaa?`,
      removeText:
        'Pelaaja poistuu tietokannasta ja nimimerkki vapautuu. Pelaajan koodit lakkaavat toimimasta ja tilalle tulee uudet vapaat koodit samoihin eriin; odottavat ilmoittautumiset poistuvat. Käynnissä olevan pelin pelaajia ei poisteta.',
      removed: (nick: string) => `Pelaaja ${nick} poistettu.`,
      removedAll: (removed: number, kept: number) =>
        `Poistettu ${removed} pelaajaa.${kept > 0 ? ` ${kept} jäi (käynnissä olevassa pelissä tai koodeja lähetetään).` : ''}`,
    },
    // Automatic bingo and sponsor ads (decisions 2026-09-30).
    autoBingo: {
      tabs: { batches: 'Kuponkierät', host: 'Juonnettu arvonta', auto: 'Automaattinen bingo', ads: 'Mainoskaruselli', fillers: 'Välilauseet', sms: 'Koodien jakelu', players: 'Koodit ja pelaajat', logs: 'Lokit', audio: 'Äänitiedostot' } as Record<string, string>,
      loading: 'Ladataan…',
      runningTitle: 'Automaattinen bingo',
      starting: 'Alkukuulutukset soivat…',
      pausedBadge: 'Tauolla',
      pause: 'Tauko',
      resume: 'Jatka arvontaa',
      claims: (s: number) => `BINGO! painettu – tarkistusaikaa ${s} s`,
      runningInfo:
        'Palvelin arpoo numerot ja äänet lukevat ne. Pelaajat painavat itse BINGO!-nappia; ensimmäinen hyväksytty painallus pysäyttää arvonnan 10 sekunniksi, ja saman tai uudemman numeron bingot jakavat voiton.',
      title: 'Automaattinen bingo',
      info: 'Palvelin arpoo numerot valitulla tahdilla ja nauhoitetut äänet lukevat ne pelaajan sivulla ja lähetysnäkymässä. Pelin voi ajastaa tai aloittaa heti. Ajastetussa pelissä kuulutetaan 10 ja 5 minuuttia ennen, sitten "minuutin kuluttua" ja alkumusiikki, ja ensimmäinen numero arvotaan ajastettuun aikaan.',
      interval: 'Arvontatahti, sekuntia numeroiden välillä (5–60)',
      closeBefore: 'Kuponkien haku sulkeutuu, minuuttia ennen alkua',
      minCoupons: 'Kuponkeja vähintään (muuten arvonta perutaan)',
      maxCoupons: 'Kuponkeja enintään (täynnä → kuponkien haku sulkeutuu)',
      announce: 'Aulan ilmoitus ajastetusta pelistä',
      announceAuto: 'Automaattisesti (Seuraava arvonta -ilmoitus tehdään ajastuksesta)',
      announceManual: 'Ilmoitan itse (Juonnettu arvonta → Seuraava arvonta)',
      music: 'Alkumusiikki "minuutin kuluttua" -kuulutuksen jälkeen',
      background: 'Hiljainen taustamusiikki pelin aikana',
      waitMusic: 'Odotusajan musiikki (alkaa 30 min ennen arvontaa)',
      waitSource: 'Odotusmusiikki',
      orderList: 'Kappaleet listan järjestyksessä',
      orderRandom: 'Kappaleet satunnaisessa järjestyksessä',
      sourceStream: 'Audiostriimi',
      sourceFile: 'Oma taustamusiikki',
      instructions: 'Ohjeet ääneen, kun kuponkien haku sulkeutuu',
      instructionsMissing: 'Ohjeiden äänitiedosto (ohjeet.mp3) puuttuu vielä – kytkin alkaa toimia, kun nauhoitus on lisätty sovellukseen.',
      adsTitle: 'Sponsorimainokset',
      adsOn: 'Mainokset käytössä (Mainoskaruselli-välilehden asetusten mukaan)',
      adsOff: 'Ei mainoksia (karusellin mainokset ja asetukset säilyvät, mutta mainoksia ei soiteta)',
      carouselSummary: (n: number, all: number) => `Karusellissa ${n} / ${all} mainosta. Soittotapa, määrät ja tauot asetetaan Mainoskaruselli-välilehdellä.`,
      jackpot: 'Superjackpot: täysi kuponki viimeistään 52. numerolla voittaa superjackpotin',
      jackpotState: (state: string, draws: number) =>
        state === 'open' ? `Superjackpot mahdollinen ${draws}. numeroon asti` : state === 'won' ? 'Superjackpot voitettu' : 'Superjackpot ei lähtenyt',
      withImage: 'kuva',
      batches: 'Kuponkierät peliin',
      selectedPlayers: (n: number) => `Haettuja kuponkeja nyt ${n}`,
      when: 'Ajastus: ensimmäinen numero arvotaan (tyhjä = ei ajastusta)',
      whenInfo: 'Ilman ajastusta asetukset vain tallennetaan, ja pelin voi aloittaa heti alla olevalla painikkeella.',
      whenPast: 'Ajastuksen pitää olla vähintään minuutin päässä.',
      chooseBatch: 'Valitse ajastettuun peliin vähintään yksi kuponkierä.',
      plan: (start: string, intro: string, close: string) =>
        `Ajastettu: ensimmäinen numero ${start}. Kuponkien haku sulkeutuu ${close}, "minuutin kuluttua" -kuulutus ${intro}.`,
      save: 'Tallenna asetukset',
      saveSchedule: 'Tallenna ja ajasta',
      removeSchedule: 'Poista ajastus',
      saved: 'Asetukset tallennettu.',
      scheduled: (when: string) => `Automaattinen bingo ajastettu: ${when}.`,
      startNow: 'Aloita heti valituilla erillä',
      startNowInfo: 'Heti aloitettaessa ei ole ennakkokuulutuksia eikä alkumusiikkia: tervetulotoivotus, mainos, vaihe ja ensimmäinen numero.',
      startedNow: 'Automaattinen bingo alkoi.',
      blockedHost: 'Juonnettu peli on käynnissä (paneeli ylhäällä). Lopeta se ensin "Lopeta peli" -painikkeella, niin voit aloittaa automaattipelin.',
      blockedAuto: 'Automaattinen peli on jo käynnissä.',
      blockedChoose: 'Valitse ensin peliin tuleva kuponkierä (Kuponkierät peliin).',
      blockedNoCoupons: 'Valituista eristä ei ole haettu yhtään kuponkia.',
      adTexts: {
        new: 'Lisää mainos',
        newInfo:
          'Mainos on MP3-ääni (enintään 6 Mt) ja valinnainen kuva (PNG, JPG tai WebP, enintään 1 Mt), joka näkyy pelaajan sivulla ja lähetysnäkymässä äänen soidessa. Äänenvoimakkuus tasataan automaattisesti. Mainos pysyy palvelimella, kunnes poistat sen.',
        name: 'Nimi (esim. sponsorin nimi)',
        title: 'Sponsorimainokset',
        empty: 'Ei vielä mainoksia.',
        saved: (name: string) => `Mainos ${name} tallennettu.`,
        removed: (name: string) => `Mainos ${name} poistettu.`,
      },
      // Fillers: phrases and jokes between the draws (user request 2026-10-01).
      fillerTexts: {
        new: 'Lisää välilause',
        newInfo:
          'Välilause tai vitsi on MP3-ääni (enintään 6 Mt), joka soi automaattibingossa arvonnan lomassa, jos välilauseet on kytketty päälle Automaattinen bingo -välilehdellä. Lauseet soivat satunnaisessa järjestyksessä, eikä samaa toisteta ennen kuin kaikki on kuultu. Äänenvoimakkuus tasataan puheen tasolle automaattisesti.',
        name: 'Nimi (esim. vitsin aihe)',
        title: 'Välilauseet ja vitsit',
        empty: 'Ei vielä välilauseita.',
        saved: (name: string) => `Välilause ${name} tallennettu.`,
        removed: (name: string) => `Välilause ${name} poistettu.`,
      },
      fillers: (n: number) => `Välilauseet ja vitsit arvonnan lomassa (kirjastossa ${n} kpl, Välilauseet-välilehti)`,
      fillerEvery: 'Välilause, kun on arvottu näin monta numeroa (2–75)',
      adAudio: 'Ääni (MP3)',
      adImage: 'Kuva (valinnainen)',
      upload: 'Tallenna',
      uploading: 'Tallennetaan…',
      audioTooBig: 'Äänitiedosto on liian suuri (enintään 6 Mt).',
      imageTooBig: 'Kuva on liian suuri (enintään 1 Mt).',
      audioBad: 'Äänitiedostoa ei voitu lukea. Käytä MP3-tiedostoa.',
      adMeta: (length: string, volume: number, image: boolean) => `${length} · äänenvoimakkuus ${volume} %${image ? ' · kuva' : ''}`,
      removeTitle: (name: string) => `Poistetaanko mainos ${name}?`,
      removeText: 'Mainos poistetaan palvelimelta ja automaattibingon asetuksista.',
    },
    // Areena Bingo (decision 2026-09-29).
    bingo: {
      gameTitle: 'Arvonta',
      startInfo: 'Valitse peliin mukaan tulevat kuponkierät (esim. paperilla ja sähköisesti jaetut koodit). Sulje ensin kuponkien haku ja aloita sitten peli – jos haku on vielä auki, aloitus sulkee sen.',
      chooseBatches: 'Kuponkierät peliin',
      range: (from: number, to: number) => `kupongit ${from}–${to}`,
      selectedInfo: (batches: number, players: number) => `Valittu ${batches} erä${batches === 1 ? '' : 'ä'} · haettuja kuponkeja ${players}`,
      closeSelected: (n: number) => (n === 0 ? 'Kuponkien haku suljettu' : 'Sulje kuponkien haku'),
      autoClose: 'Kuponkien haku sulkeutuu viimeistään, kun peli aloitetaan.',
      label: 'Kuponkierän tunniste',
      start: 'Aloita peli',
      next: 'Seuraava numero',
      wait: (s: number) => `Odota ${s} s`,
      stage: (label: string) => `Haetaan: ${label}`,
      drawn: (n: number, players: number) => `Arvottu ${n}/75 · kuponkeja pelissä ${players}`,
      last: 'Viimeisin numero',
      noDraws: 'Paina "Seuraava numero" aloittaaksesi arvonnan.',
      bingoTitle: 'BINGO',
      bingoNone: 'Ei vielä bingoa tässä vaiheessa.',
      callsTitle: (n: number) => (n === 1 ? 'Pelaaja huusi BINGO!' : `${n} pelaajaa huusi BINGO!`),
      callsHint: 'Pelaaja painoi BINGO!-nappia, ja palvelin totesi kupongilla olevan bingon. Huutaneet kupongit on valittu valmiiksi – vahvista voittaja alta.',
      calledBadge: 'huusi',
      confirm: (n: number) => (n === 1 ? 'Vahvista voittaja' : `Vahvista ${n} voittajaa`),
      confirmHint: 'Vahvistus näyttää BINGO!-ruudun lähetyksessä ja siirtää pelin seuraavaan vaiheeseen (täyden kupongin jälkeen peli päättyy).',
      nearTitle: 'Lähellä voittoa',
      nearNone: 'Kukaan ei ole vielä lähellä.',
      needs: (numbers: string) => `Puuttuu: ${numbers}`,
      nearMore: (n: number) => `… ja ${n} muuta kuponkia lähellä.`,
      missing: (n: number) => (n === 1 ? '1 puuttuu' : `${n} puuttuu`),
      cardLabel: (n: number, nick: string) => `Kuponki ${n} – ${nick}`,
      winners: 'Voittajat',
      end: 'Lopeta peli',
      endTitle: 'Lopetetaanko bingopeli?',
      endText: 'Peli päättyy pysyvästi nykyiseen tilanteeseen, eikä arvontaa voi enää jatkaa. Jos haluat vain tauon, älä lopeta peliä – peli odottaa, kunnes arvot seuraavan numeron.',
      endWord: 'LOPETA',
      runningBar: 'Bingopeli on käynnissä.',
      nextTitle: 'Seuraava arvonta',
      nextInfo: 'Ilmoitus näkyy Areenan aulassa ja julkisella bingosivulla painikkeen kanssa, josta pääsee hakemaan kupongin. Ilmoitus poistuu itsestään 3 tuntia ilmoitetun ajan jälkeen.',
      nextWhen: 'Päivä ja kellonaika',
      nextText: 'Lisätieto (valinnainen)',
      nextTextPlaceholder: 'esim. Perjantairadion lähetyksessä',
      nextSave: 'Tallenna ilmoitus',
      nextRemove: 'Poista ilmoitus',
      nextSaved: 'Seuraavan arvonnan ilmoitus tallennettu.',
      nextRemoved: 'Ilmoitus poistettu.',
      nextShown: (when: string) => `Aulassa näkyy nyt: seuraava bingoarvonta ${when}`,
      backToGame: 'Jatka juontamista',
      endType: 'Vahvista kirjoittamalla LOPETA',
      finished: 'Peli päättyi.',
      newGame: 'Uusi peli',
      obsTitle: 'Lähetys (OBS Studio)',
      obsInfo:
        'OBS: Lähteet → + → Selain → liitä osoite, leveys 1920 ja korkeus 1080, ✓ "Ohjaa ääntä OBS:n kautta". Tausta: tumma, läpinäkyvä (päälle omaa kuvaa) tai vihreä (chroma key).',
      obsLink: (bg: string) => `Lähetysnäkymä, tausta: ${bg}`,
      backgrounds: { tumma: 'tumma', lapinakyva: 'läpinäkyvä', vihrea: 'vihreä' } as Record<string, string>,
      obsNearLink: (bg: string) => `Lähimpänä bingoa -kupongit (7 × 3), tausta: ${bg}`,
      publicLink: 'Kuponkien haku (pelaajien bingosivu ja numerotaulu)',
      batchesTitle: 'Kuponkierät',
      newBatch: 'Luo kuponkierä',
      newBatchInfo: 'Erään tulee valittu määrä kuponkeja (enintään 50) ja yhtä monta bingokoodia. Koodilla haettaessa pelaaja saa seuraavan vapaan kupongin. Kuponkien numerointi jatkuu edellisestä erästä, joten numerot ovat yksilöllisiä ja samaan peliin voi ottaa useita eriä. Pelaajat eivät näe erän tunnistetta.',
      size: 'Kuponkeja (1–50)',
      create: 'Luo erä',
      created: (label: string, n: number) => `Erä ${label}: ${n} kuponkia ja koodia luotu.`,
      labelTaken: 'Samalla tunnisteella on jo kuponkierä.',
      none: 'Ei kuponkieriä.',
      claimed: (c: number, n: number) => `haettu ${c}/${n}`,
      registrationOpen: 'kuponkien haku auki',
      registrationClosed: 'kuponkien haku suljettu',
      inGame: 'pelissä',
      openRegistration: 'Avaa kuponkien haku',
      closeRegistration: 'Sulje kuponkien haku',
      showCodes: 'Koodit ja kupongit',
      hideCodes: 'Piilota',
      copyFree: 'Kopioi vapaat koodit',
      copied: (n: number) => `${n} koodia kopioitu.`,
      noFree: 'Kaikki koodit on haettu.',
      print: 'Tulosta koodit',
      save: 'Tallenna koodit',
      remove: 'Poista',
      removeTitle: (label: string) => `Poistetaanko kuponkierä ${label}?`,
      removeText: 'Erä, sen kupongit, koodit ja pelit (arvotut numerot ja voittajat) poistetaan pysyvästi, samoin bingopelaajat, joille ei jää yhtään kuponkia. Jos erä on valittu automaattibingon ajastukseen, se poistuu ajastuksesta.',
      free: 'vapaa',
      smsSent: '📨 lähetetty viestinä, ei vielä haettu',
      reservedFor: (nick: string) => `🔖 varattu: ${nick} (ei vielä aktivoitu)`,
      // Closing the claiming voids the absent players' coupons (user decision 2026-10-02).
      voidTitle: (n: number) => `Mitätöidäänkö ${n} ${n === 1 ? 'kuponki tai koodi' : 'kuponkia tai koodia'}?`,
      voidConfirm: 'Sulje haku ja mitätöi',
      voidText:
        'Kun kuponkien haku sulkeutuu, mukaan jäävät vain paikalla olevat pelaajat (bingosivulla viimeisen 2 tunnin aikana). Poissaolevien kupongit ja aktivoimattomat varatut koodit mitätöityvät: vanhat koodit lakkaavat toimimasta ja erään tulee tilalle uudet vapaat koodit. Tätä ei voi perua.',
      cardTitle: 'Tulosta tai tallenna bingokoodit',
      qr: 'QR-koodi kortteihin (vie suoraan kupongin hakuun)',
      card: {
        heading: 'Areena Bingo',
        code: 'Bingokoodi',
        register: 'Hae kuponkisi osoitteessa',
        scan: 'tai lue QR-koodi puhelimella',
        valid: 'Kertakäyttöinen koodi – hae kuponki ennen arvontaa',
        slogan: 'Hae koodilla oma bingokuponkisi ennen arvontaa!',
      },
      // Codes as text files in a ZIP (user request 2026-09-30).
      formatTxt: 'Teksti – jokainen koodi omana tekstitiedostonaan ohjeineen, kaikki yhdessä ZIP-paketissa',
      textFile: (code: string, spacedCode: string, site: string, url: string) =>
        [
          'AREENA BINGO',
          '============',
          '',
          `Bingokoodisi: ${spacedCode}`,
          '',
          'Näin haet oman bingokuponkisi:',
          `1. Mene osoitteeseen ${site}`,
          `   (tai avaa suoraan: ${url})`,
          '2. Kirjoita bingokoodisi ja valitse itsellesi nimimerkki.',
          '3. Saat oman bingokupongin. Pidä se esillä arvonnan aikana puhelimella',
          '   tai tulosta se.',
          '4. Seuraa arvontaa ja merkitse arvotut numerot kuponkiisi.',
          '   Kun saat bingon, ilmoita siitä juontajalle.',
          '',
          'Koodi on henkilökohtainen: samalla koodilla saat aina saman kupongin.',
          'Hae kuponki ennen arvontaa.',
          '',
          `Koodi ilman välilyöntiä: ${code}`,
        ].join('\r\n'),
      textList: (label: string, site: string, codes: string[]) =>
        [`Areena Bingo – kuponkierä ${label}`, `Kupongin haku: ${site}`, '', ...codes.map((c, i) => `${i + 1}. ${c}`)].join('\r\n'),
      textListName: 'kaikki-koodit.txt',
      sponsorTitle: 'Sponsori keskiruudussa',
      sponsorInfo: 'Kuva näkyy kaikkien kuponkien vapaassa keskiruudussa (verkko, tuloste, PDF, kuva). PNG, JPG tai WebP, enintään 300 kt.',
      sponsorPick: 'Valitse kuva',
      sponsorRemove: 'Poista kuva',
      sponsorSaved: 'Sponsorin kuva tallennettu.',
      sponsorRemoved: 'Sponsorin kuva poistettu.',
      sponsorTooBig: 'Kuva on liian suuri (enintään 300 kt).',
      sponsorType: 'Vain PNG-, JPG- tai WebP-kuva.',
    },
    radio: {
      title: 'Musiikki (radio)',
      info: 'Musiikkiluvan saaneet pelaajat kuulevat tämän Icecast-striimin. Selain soittaa striimin suoraan striimipalvelimelta – se ei kulje Areenan eikä Cloudflaren kautta. Radio soi vain, kun asemalla on lähetys; muulloin soitin yrittää uudelleen minuutin välein.',
      enabled: 'Radio käytössä',
      name: 'Aseman nimi',
      url: 'Striimin osoite',
      urlHint: 'Vain https-osoite kelpaa (selain estää salaamattoman äänen https-sivulla), esim. https://perjantairadio.eu/live',
      httpsOnly: 'Striimin osoitteen on alettava https://',
      permissions: 'Musiikkiluvat annetaan pelaajakohtaisesti: Ylläpito → Käyttäjät → 🎵 Anna musiikkilupa. Ylläpitäjät kuulevat radion aina, vieraat alla olevan asetuksen mukaan.',
      saved: 'Musiikkiasetukset tallennettu.',
      publicTitle: 'Julkinen soitin',
      publicPlayer: 'Julkinen soitin käytössä – kuka tahansa linkin saanut voi kuunnella ilman tunnusta',
      publicInfo: 'Soitin on osoitteessa areena.eu/kuuntele/. Siinä on Areenan mainokset ja maininta yhteistyöstä Perjantairadion kanssa. Puhelimessa kuuntelu jatkuu, vaikka näyttö lukittuu.',
      publicLink: 'Soittimen linkki',
      guestTitle: 'Vieraat',
      guestRadio: 'Vieraskoodilla tulevat kuulevat radion',
      guestRadioInfo: 'Vieras saa saapuessaan tämän hetkisen asetuksen mukaisen musiikkiluvan koko vierailunsa ajaksi (enintään 3 h). Muutos koskee vain uusia vieraita, ei jo sisällä olevia. Vieraille ei anneta erillisiä musiikkilupia.',
    },
    backup: {
      title: 'Google Drive -varmuuskopio',
      info: 'Palvelin tarkistaa joka yö klo 4.15, onko jokin muuttunut, ja lähettää silloin varmuuskopion Driveen (kslahti@gmail.com, kansio Areena-varmuuskopiot). Ilman muutoksia kopio lähetetään kerran viikossa. 30 viimeisintä säilytetään.',
      last: (when: string, size: string) => `Viimeisin varmuuskopio: ${when} (${size}).`,
      none: 'Varmuuskopiota ei ole vielä otettu.',
      old: 'Varoitus: edellisestä varmuuskopiosta on yli 8 päivää. Tarkista palvelimelta: journalctl -u areena-drive-backup',
    },
    closure: {
      title: 'Pelisalin sulkeminen',
      info: 'Huoltotauon aikana pelaajat näkevät huoltosivun viesteineen eivätkä voi pelata eikä kirjoittaa chattiin. Tauon alkaessa kaikki käynnissä olevat pelit keskeytetään (ei lasketa tilastoihin). Ylläpitäjä voi käyttää pelisalia normaalisti.',
      stateOpen: 'Pelisali on auki.',
      stateClosed: 'Huoltotauko on käynnissä – pelisali on suljettu.',
      stateScheduled: (when: string) => `Huoltotauko ajastettu: ${when}. Pelaajat näkevät ennakkoilmoituksen.`,
      stateDraining: (when: string) => `Huoltotauko alkaa ${when}. Uusia pelejä ei voi aloittaa; käynnissä olevat jatkuvat siihen asti.`,
      message: 'Viesti pelaajille',
      presets: 'Vakioviestit',
      usePreset: 'Käytä',
      removePreset: 'Poista vakioviesti',
      addPreset: 'Tallenna viesti vakioviestiksi',
      start: 'Tauon alku',
      startNow: 'Heti',
      startNowHint: 'Käynnissä olevat pelit keskeytetään heti.',
      startDelay: 'Viiveellä',
      delayMinutes: 'minuutin kuluttua',
      startDelayHint: 'Uusia pelejä ei voi aloittaa. Käynnissä olevat jatkuvat, ja pelaajat näkevät lähtölaskennan myös pelin aikana. Tauon alkaessa keskeneräiset pelit keskeytetään.',
      startAt: 'Kellonaikaan',
      drainBefore: 'Uudet pelit estetään (min ennen)',
      startAtHint: 'Pelaajat näkevät ennakkoilmoituksen. Uusia pelejä ei voi aloittaa valittua aikaa ennen taukoa.',
      end: 'Tauon loppu',
      endManual: 'Kun avaan käsin',
      endAt: 'Automaattisesti',
      closeNow: 'Aloita huoltotauko',
      schedule: 'Ajasta huoltotauko',
      update: 'Päivitä',
      open: 'Avaa pelisali',
      saved: 'Tallennettu.',
      messageRequired: 'Kirjoita viesti pelaajille.',
      badTimes: 'Tarkista ajat: tauon on alettava tulevaisuudessa ja päätyttävä alkamisen jälkeen.',
      confirmTitle: 'Aloitetaanko huoltotauko heti?',
      confirmText: (games: number) =>
        `Pelisali suljetaan heti.${games > 0 ? ` Käynnissä olevat pelit (${games}) keskeytetään eikä niitä lasketa tilastoihin.` : ''}`,
    },
    pm: {
      title: 'Yksityisviestit (30 päivää)',
      none: 'Ei yksityisviestejä.',
      messages: (n: number) => `${n} viestiä`,
      show: 'Näytä',
      hide: 'Piilota',
      info: 'Ylläpitäjä voi lukea yksityisviestit valvontaa varten. Pelaajille kerrotaan tästä chatissa.',
    },
    settings: {
      title: 'Peliareenan asetukset',
      info: 'Muutokset koskevat uusia pelejä. Käynnissä olevat pelit jatkuvat alkuperäisillä asetuksilla.',
      timersTitle: 'Pelikohtaiset ajat',
      turnTime: 'Vuoroaika (s)',
      turnTimeAtTable: 'Avaaja valitsee pöydässä',
      timeBank: 'Aikapankki (s)',
      placementTime: 'Laivojen sijoitusaika (sekuntia)',
      startingPlayer: 'Aloittaja',
      starting: { challenger: 'Haastaja', challenged: 'Haastettu', random: 'Satunnainen' },
      maxActiveGames: 'Samanaikaisten pelien enimmäismäärä',
      hitGrantsExtraShot: 'Osuma antaa lisälaukauksen',
      forbiddenAreaVisible: 'Näytä kielletty alue sijoituksessa',
      markSunkSurroundings: 'Merkitse upotetun laivan ympäristö',
      lobbyChatRetention: 'Aulan chatin säilytysaika (tuntia, enintään 720 = 30 vrk)',
      retention: 'Otteluhistorian säilytys',
      retentionForever: 'Säilytetään toistaiseksi',
      retentionDays: 'Säilytysaika (päivää)',
      save: 'Tallenna asetukset',
      saved: 'Asetukset tallennettu.',
      historyTitle: 'Otteluhistoria',
      historyInfo: 'Tyhjentää päättyneiden pelien historian. Pelaajien tilastot säilyvät.',
      clearHistory: 'Tyhjennä historia',
      clearConfirmTitle: 'Tyhjennetäänkö otteluhistoria?',
      clearConfirmText: 'Kaikki päättyneet pelit poistetaan pysyvästi. Tilastot säilyvät.',
      cleared: (n: number) => `Historia tyhjennetty (${n} peliä poistettu).`,
    },
    games: {
      title: 'Käynnissä olevat pelit',
      none: 'Ei käynnissä olevia pelejä.',
      refresh: 'Päivitä',
      started: 'Alkoi',
      showChat: 'Näytä chat',
      hideChat: 'Piilota chat',
      noChat: 'Ei chat-viestejä.',
      abort: 'Keskeytä peli',
      abortConfirmTitle: 'Keskeytetäänkö peli?',
      abortConfirmText: 'Peli päättyy ilman voittajaa eikä sitä lasketa tilastoihin.',
      aborted: 'Peli keskeytetty.',
      status: { placement: 'Sijoitus', playing: 'Käynnissä' },
      computer: 'tietokone',
    },
  },
};

export type Texts = typeof fi;
